import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "../App";
import { getAdminToken } from "../components/Auth";
import { toast } from "sonner";
import { 
  DollarSign, TrendingUp, Users, Cpu, AlertCircle,
  ArrowUpRight, ArrowDownRight, Pause, RefreshCw, Wifi, WifiOff,
  AlertTriangle, Server, ChevronDown, ChevronUp, Thermometer,
  Fan, Power, RotateCcw, Monitor, Zap, X, Download, Upload, Pin, PinOff, Wrench
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, Legend 
} from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import BoardLookup from "../components/BoardLookup";

// Live-panel hashrates arrive in GH/s; KAS/BTC rigs run to thousands of them.
const fmtGH = (v) => (v >= 1000 ? `${(v / 1000).toFixed(2)} TH/s` : `${v.toFixed(1)} GH/s`);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [machineMonitor, setMachineMonitor] = useState(null);
  const [f2poolBreakdown, setF2poolBreakdown] = useState(null);
  const [f2poolWorkers, setF2poolWorkers] = useState([]);
  const [f2poolShow, setF2poolShow] = useState(null);   // "online" | "offline" | null
  const openF2poolList = async (which) => {
    setF2poolShow(f2poolShow === which ? null : which);
    if (f2poolWorkers.length === 0) {
      try { const r = await axios.get(`${API}/admin/f2pool-workers`); setF2poolWorkers(r.data?.workers || []); } catch {}
    }
  };
  const [coinShow, setCoinShow] = useState(null);       // { coin: "LTC", status: "online" | "offline" } | null
  const openCoinList = (coin, status) => {
    setCoinShow(coinShow?.coin === coin && coinShow.status === status ? null : { coin, status });
    if (f2poolBreakdown?.enabled && f2poolWorkers.length === 0) {
      axios.get(`${API}/admin/f2pool-workers`).then(r => setF2poolWorkers(r.data?.workers || [])).catch(() => {});
    }
  };
  const refreshF2pool = async () => {
    try { const r = await axios.get(`${API}/admin/f2pool-workers`); setF2poolWorkers(r.data?.workers || []); } catch {}
    axios.get(`${API}/admin/f2pool-breakdown`).then(r => setF2poolBreakdown(r.data)).catch(() => {});
  };
  const hideF2poolWorker = async (w) => {
    if (!window.confirm(`Hide offline worker "${w.name}" (${w.coin}) from the dashboard?\n\nThis only hides the entry — it does NOT touch the miner or F2Pool. It comes back if the worker starts hashing again.`)) return;
    try {
      await axios.post(`${API}/admin/f2pool-hide-worker`, { name: w.name, coin: w.coin });
      toast.success(`Hidden: ${w.name} (${w.coin})`);
      await refreshF2pool();
    } catch (e) {
      toast.error(`Failed to hide worker: ${e?.response?.data?.detail || e.message}`);
    }
  };
  const [monitorLoading, setMonitorLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showOnline, setShowOnline] = useState(false);  // Toggle for online machines
  const [expandedAccount, setExpandedAccount] = useState(null);  // Track which account is expanded
  const [monitorMode, setMonitorMode] = useState("api");  // "api" or "watcher" - default api
  const [overdueData, setOverdueData] = useState(null);
  const [liveMachines, setLiveMachines] = useState([]);
  const [liveMachinesLoading, setLiveMachinesLoading] = useState(false);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [hiddenMachines, setHiddenMachines] = useState([]);
  const [offlineHiddenWorkers, setOfflineHiddenWorkers] = useState([]);
  const [showHiddenPanel, setShowHiddenPanel] = useState(false);
  const [liveFilter, setLiveFilter] = useState("customers");
  const [liveSearch, setLiveSearch] = useState("");
  const [boardsOnly, setBoardsOnly] = useState(false);
  const [walletSwitch, setWalletSwitch] = useState({ switched: false });
  const [newWallet, setNewWallet] = useState("");
  const [selectedMachines, setSelectedMachines] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [mismatch, setMismatch] = useState(null);
  const [mismatchLoading, setMismatchLoading] = useState(false);
  const [mismatchOpen, setMismatchOpen] = useState(false);
  const [recentCmds, setRecentCmds] = useState([]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [onlineCustomers, setOnlineCustomers] = useState([]);
  const [onlineTab, setOnlineTab] = useState("now"); // "now" | "24h"
  const [recentOnline, setRecentOnline] = useState([]);
  const [recentOnlineLoading, setRecentOnlineLoading] = useState(false);
  const [sessionHistory, setSessionHistory] = useState(null); // { customer_id, name, sessions } | null
  const [sessionHistoryLoading, setSessionHistoryLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [roster, setRoster] = useState(null); // { roster, totals } | null
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterSearch, setRosterSearch] = useState("");
  const [newRoster, setNewRoster] = useState({ name: "", model: "L9", total: 1, online: 1 });
  const [rosterSuggestions, setRosterSuggestions] = useState([]);
  const [rosterReconcile, setRosterReconcile] = useState(null); // { unlisted, unlisted_machines, live_online } | null
  const [rosterStatusFilter, setRosterStatusFilter] = useState(null); // null | "online" | "offline"
  const [rosterModelFilter, setRosterModelFilter] = useState(null);   // null | a model name
  const [typeBreakdown, setTypeBreakdown] = useState(null); // { name, loading, rows } | null
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  const [machineTypeGroups, setMachineTypeGroups] = useState([]);
  const [allMachineTypes, setAllMachineTypes] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupSelectedIds, setNewGroupSelectedIds] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [editGroupSelectedIds, setEditGroupSelectedIds] = useState([]);
  const typeBreakdownScrollRef = useRef(null);

  // The modal's scrollable body was opening already scrolled a bit down (the
  // price-tier chips row peeking out from under the sticky header) instead
  // of showing the top of the content -- force it back to scrollTop 0 every
  // time the data goes from loading to loaded (or a different card is opened).
  useEffect(() => {
    if (typeBreakdownScrollRef.current) {
      typeBreakdownScrollRef.current.scrollTop = 0;
    }
  }, [typeBreakdown?.name, typeBreakdown?.loading]);

  const parseUptimeToSeconds = (u) => {
    if (!u) return -1;
    if (typeof u === "number") return u;
    const s = String(u).toLowerCase().trim();
    if (!s || s === "—") return -1;
    let total = 0;
    const re = /(\d+(?:\.\d+)?)\s*(d|h|m|s)/g;
    let match, found = false;
    while ((match = re.exec(s)) !== null) {
      found = true;
      const n = parseFloat(match[1]);
      const unit = match[2];
      if (unit === "d") total += n * 86400;
      else if (unit === "h") total += n * 3600;
      else if (unit === "m") total += n * 60;
      else total += n;
    }
    if (found) return total;
    const asNum = parseFloat(s);
    return isNaN(asNum) ? -1 : asNum;
  };

  const parseIpForSort = (ip) => {
    if (!ip) return [0, 0, 0, 0];
    const parts = String(ip).split(".").map(p => parseInt(p, 10) || 0);
    while (parts.length < 4) parts.push(0);
    return parts;
  };

  const getSortValue = (m, key) => {
    switch (key) {
      case "status": return m.status || "";
      case "ip": return parseIpForSort(m.ip);
      case "worker": return (m.worker_name || "").toLowerCase();
      case "model": return (m.model || "").toLowerCase();
      case "farm": return (m.farm || "").toLowerCase();
      case "hashrate": return Number(m.hashrate) || 0;
      case "temp": return Number(m.temperature) || 0;
      case "fan": return Number(m.fan_speed) || 0;
      case "power": return Number(m.power) || 0;
      case "uptime": return parseUptimeToSeconds(m.uptime);
      case "crashes": return Number(m.crash_count_24h) || 0;
      default: return 0;
    }
  };

  const compareSort = (a, b, key, dir) => {
    const av = getSortValue(a, key);
    const bv = getSortValue(b, key);
    let cmp = 0;
    if (Array.isArray(av) && Array.isArray(bv)) {
      for (let i = 0; i < Math.max(av.length, bv.length); i++) {
        const d = (av[i] || 0) - (bv[i] || 0);
        if (d !== 0) { cmp = d; break; }
      }
    } else if (typeof av === "number" && typeof bv === "number") {
      cmp = av - bv;
    } else {
      cmp = String(av).localeCompare(String(bv));
    }
    return dir === "desc" ? -cmp : cmp;
  };

  // The stat tiles count this list, so typing "L9" shows how many L9s are
  // online, not the whole farm. The Missing boards toggle isn't applied here --
  // it would turn every tile into a count of the short-board machines.
  const searchedLiveMachines = useMemo(() => {
    const q = liveSearch.trim().toLowerCase();
    if (!q) return liveMachines;
    return liveMachines.filter(m =>
      (m.worker_name || '').toLowerCase().includes(q) ||
      (m.ip || '').includes(q) ||
      (m.farm || '').toLowerCase().includes(q) ||
      (m.model || '').toLowerCase().includes(q) ||
      (m.mac || '').toLowerCase().includes(q) ||
      (m.sn || '').toLowerCase().includes(q) ||
      (m.psu_sn || '').toLowerCase().includes(q));
  }, [liveMachines, liveSearch]);

  const visibleLiveMachines = useMemo(() => {
    return searchedLiveMachines
      .filter(m => !boardsOnly || m.boards_short)
      .sort((a, b) => {
        if (sortKey) return compareSort(a, b, sortKey, sortDir);
        return (a.status === 'online' ? 1 : -1) - (b.status === 'online' ? 1 : -1);
      });
  }, [searchedLiveMachines, sortKey, sortDir, boardsOnly]);

  const handleSort = (key) => {
    if (sortKey === key) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortKey(null); setSortDir("asc"); }
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortArrow = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-600 ml-1">⇅</span>;
    return <span className="text-cyan-400 ml-1">{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  // Format an online duration (in seconds) as "2h 14m", "14m 3s", etc.
  const formatOnlineDuration = (startIso, lastSeenIso) => {
    if (!startIso) return "—";
    const start = new Date(startIso).getTime();
    if (isNaN(start)) return "—";
    // Count up live from session start to "now", but never beyond last_seen + a small grace
    const lastSeenRaw = lastSeenIso ? new Date(lastSeenIso).getTime() : nowTick;
    const lastSeen = isNaN(lastSeenRaw) ? nowTick : lastSeenRaw;
    const end = Math.min(nowTick, lastSeen + 120000);
    let secs = Math.max(0, Math.floor((end - start) / 1000));
    const h = Math.floor(secs / 3600);
    secs -= h * 3600;
    const m = Math.floor(secs / 60);
    const s = secs - m * 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  // Lightweight User-Agent -> "Browser on Platform" label. Not exhaustive, just
  // enough to tell devices apart at a glance (e.g. "Chrome on iPhone" vs
  // "Safari on Mac") without pulling in a UA-parsing library for this one spot.
  const parseUserAgent = (ua) => {
    if (!ua) return "Unknown device";
    let browser = "Browser";
    if (/edg\//i.test(ua)) browser = "Edge";
    else if (/opr\//i.test(ua) || /opera/i.test(ua)) browser = "Opera";
    else if (/chrome\//i.test(ua)) browser = "Chrome";
    else if (/crios\//i.test(ua)) browser = "Chrome";
    else if (/fxios\//i.test(ua) || /firefox\//i.test(ua)) browser = "Firefox";
    else if (/safari\//i.test(ua)) browser = "Safari";

    let platform = "";
    if (/iphone/i.test(ua)) platform = "iPhone";
    else if (/ipad/i.test(ua)) platform = "iPad";
    else if (/android/i.test(ua)) platform = "Android";
    else if (/windows/i.test(ua)) platform = "Windows";
    else if (/mac os x|macintosh/i.test(ua)) platform = "Mac";
    else if (/linux/i.test(ua)) platform = "Linux";

    return platform ? `${browser} on ${platform}` : browser;
  };

  // Format a "seconds since last update" value as "3d 4h ago", "12h 30m ago", "45m ago"
  const formatDurationAgo = (seconds) => {
    if (seconds == null) return "unknown";
    if (seconds >= 86400) return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h ago`;
    if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  // Helper to format hashrate
  const formatHashrate = (hashrate, coin) => {
    if (!hashrate) return "0 H/s";
    if (coin === "ZEC") {
      // ZEC (Equihash) is measured in Sol/s — do NOT divide to GH/s
      if (hashrate >= 1e9) return (hashrate / 1e9).toFixed(2) + " GSol/s";
      if (hashrate >= 1e6) return (hashrate / 1e6).toFixed(2) + " MSol/s";
      if (hashrate >= 1e3) return (hashrate / 1e3).toFixed(2) + " KSol/s";
      return hashrate.toFixed(0) + " Sol/s";
    }
    if (coin === "KAS") {
      // KAS uses TH/s scale
      if (hashrate >= 1e12) return (hashrate / 1e12).toFixed(2) + " TH/s";
      if (hashrate >= 1e9) return (hashrate / 1e9).toFixed(2) + " GH/s";
      if (hashrate >= 1e6) return (hashrate / 1e6).toFixed(2) + " MH/s";
      return hashrate.toFixed(0) + " H/s";
    } else {
      // LTC uses GH/s scale  
      if (hashrate >= 1e12) return (hashrate / 1e12).toFixed(2) + " TH/s";
      if (hashrate >= 1e9) return (hashrate / 1e9).toFixed(2) + " GH/s";
      if (hashrate >= 1e6) return (hashrate / 1e6).toFixed(2) + " MH/s";
      return hashrate.toFixed(0) + " H/s";
    }
  };

  useEffect(() => {
    fetchStats();
    fetchMachineMonitor();
    axios.get(`${API}/admin/f2pool-breakdown`).then(r => setF2poolBreakdown(r.data)).catch(() => {});
    fetchOverdue();
    fetchLiveMachines();
    
    fetchRecentCmds();
    fetchOnlineCustomers();
    // Auto-refresh machine monitor every 60 seconds
    const interval = setInterval(() => { fetchMachineMonitor(); axios.get(`${API}/admin/f2pool-breakdown`).then(r => setF2poolBreakdown(r.data)).catch(() => {}); }, 60000);
    const interval2 = setInterval(fetchLiveMachines, 120000);
    const interval3 = setInterval(fetchRecentCmds, 30000);
    const interval4 = setInterval(fetchOnlineCustomers, 30000);
    const interval5 = setInterval(() => setNowTick(Date.now()), 1000);
    return () => { clearInterval(interval); clearInterval(interval2); clearInterval(interval3); clearInterval(interval4); clearInterval(interval5); };
  }, [monitorMode, liveFilter]);

  const fetchOnlineCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/online-customers`);
      setOnlineCustomers(res.data?.customers || []);
    } catch (e) {}
  };

  const fetchRecentOnline = async () => {
    setRecentOnlineLoading(true);
    try {
      const res = await axios.get(`${API}/admin/online-customers/recent?hours=24`);
      setRecentOnline(res.data?.customers || []);
    } catch (e) {
      toast.error("Failed to load 24h activity");
    }
    setRecentOnlineLoading(false);
  };

  const openSessionHistory = async (customerId, name) => {
    setSessionHistoryLoading(true);
    setSessionHistory({ customer_id: customerId, name, sessions: [] });
    try {
      const res = await axios.get(`${API}/admin/customer-sessions/${customerId}`);
      setSessionHistory({ customer_id: customerId, name, sessions: res.data?.sessions || [] });
    } catch (e) {
      toast.error("Failed to load login history");
    }
    setSessionHistoryLoading(false);
  };

  const fetchRecentCmds = async () => {
    try {
      const res = await axios.get(`${API}/machine-data/recent-commands?limit=15`);
      setRecentCmds(res.data?.commands || []);
    } catch (e) {}
  };

  const fetchOverdue = async () => {
    try {
      const res = await axios.get(`${API}/payments/overdue`);
      setOverdueData(res.data);
    } catch (e) {}
  };

  const fetchLiveMachines = async () => {
    try {
      const filter = liveFilter === "customers" ? "true" : "false";
      const [res, switchRes, hiddenRes, offlineHiddenRes] = await Promise.all([
        axios.get(`${API}/machine-data/live?filter_customers=${filter}`),
        axios.get(`${API}/machine-data/switch-status`),
        axios.get(`${API}/machine-data/hidden`).catch(() => ({ data: { hidden: [] } })),
        axios.get(`${API}/machine-data/offline-hidden`).catch(() => ({ data: { hidden: [] } }))
      ]);
      setLiveMachines(res.data);
      setWalletSwitch(switchRes.data);
      setHiddenMachines(hiddenRes.data?.hidden || []);
      setOfflineHiddenWorkers(offlineHiddenRes.data?.hidden || []);
    } catch (e) {}
  };

  const fetchRoster = async () => {
    setRosterLoading(true);
    try {
      const [res, sugRes, recRes] = await Promise.all([
        axios.get(`${API}/admin/machine-roster`),
        axios.get(`${API}/admin/machine-roster/suggestions`).catch(() => ({ data: { suggestions: [] } })),
        axios.get(`${API}/admin/machine-roster/reconcile`).catch(() => ({ data: null })),
      ]);
      setRoster(res.data);
      setRosterSuggestions(sugRes.data?.suggestions || []);
      setRosterReconcile(recRes.data);
    } catch (e) {
      toast.error("Failed to load machine roster");
    } finally {
      setRosterLoading(false);
    }
  };

  const addRosterEntry = async () => {
    const name = (newRoster.name || "").trim();
    if (!name) { toast.error("Enter a customer name"); return; }
    try {
      await axios.post(`${API}/admin/machine-roster`, {
        name,
        model: (newRoster.model || "").trim(),
        total: Number(newRoster.total) || 0,
        online: Number(newRoster.online) || 0,
      });
      setNewRoster({ name: "", model: "L9", total: 1, online: 1 });
      fetchRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const updateRosterEntry = async (id, patch) => {
    try {
      await axios.put(`${API}/admin/machine-roster/${id}`, patch);
      fetchRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const deleteRosterEntry = async (entry) => {
    if (!window.confirm(`Remove "${entry.name}" from the list?`)) return;
    try {
      await axios.delete(`${API}/admin/machine-roster/${entry.id}`);
      fetchRoster();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const addUnlistedToRoster = async (u) => {
    const name = (u.customer || "").trim() || (u.workers?.[0]?.worker_name || "");
    if (!name) { toast.error("Can't determine a name for this entry"); return; }
    const model = (u.workers?.[0]?.model || "").replace(/^antminer\s*/i, "").trim() || "L9";
    try {
      await axios.post(`${API}/admin/machine-roster`, { name, model, total: u.count, online: u.count });
      toast.success(`Added ${name} (${u.count}× ${model})`);
      fetchRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const handleUnhideMachine = async (ip) => {
    try {
      await axios.delete(`${API}/machine-data/hidden/${encodeURIComponent(ip)}`);
      toast.success(`${ip} unhidden — PC sync will re-create it on the next cycle`);
      fetchLiveMachines();
    } catch (e) {
      toast.error("Failed to unhide");
    }
  };

  const handleOfflineUnhide = async (worker) => {
    try {
      await axios.delete(`${API}/machine-data/offline-hidden/${encodeURIComponent(worker)}`);
      toast.success(`${worker} will show again`);
      fetchLiveMachines();
    } catch (e) {
      toast.error("Failed to restore");
    }
  };

  const fetchSyncMismatch = async (forceRefresh = false) => {
    setMismatchLoading(true);
    try {
      const params = new URLSearchParams();
      if (forceRefresh) params.set("force_refresh", "true");
      if (monitorMode) params.set("mode", monitorMode);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/admin/sync-mismatch${qs}`);
      setMismatch(res.data);
      setMismatchOpen(true);
    } catch (e) {
      toast.error("Failed to load sync mismatch");
    } finally {
      setMismatchLoading(false);
    }
  };

  const handleExportMachines = () => {
    // Respect the current "My Customers" / "All" tab
    const filter = liveFilter === "customers" ? "true" : "false";
    const url = `${API}/machine-data/export-csv?filter_customers=${filter}`;
    window.open(url, "_blank");
    toast.success(`Exporting ${liveFilter === "customers" ? "customer machines" : "all machines"}...`);
  };

  const handleImportMachines = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,text/csv";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length < 2) { toast.error("CSV is empty"); return; }
        const header = lines[0].toLowerCase().split(",");
        const ipIdx = header.findIndex(h => h.trim() === "ip");
        const wnIdx = header.findIndex(h => h.trim() === "worker_name");
        if (ipIdx < 0 || wnIdx < 0) {
          toast.error("CSV must have 'ip' and 'worker_name' columns");
          return;
        }
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const parts = lines[i].split(",");
          const ip = (parts[ipIdx] || "").trim();
          const wn = (parts[wnIdx] || "").trim();
          if (ip && wn) rows.push({ ip, worker_name: wn });
        }
        if (!rows.length) { toast.error("No valid rows in CSV"); return; }

        const apply = window.confirm(
          `Found ${rows.length} machines in CSV.\n\n` +
          `Click OK to BOTH update database labels AND queue change_worker commands ` +
          `(physical miners get reconfigured back to the original worker names).\n\n` +
          `Click Cancel to ONLY update database labels (no commands sent to miners).`
        );

        const res = await axios.post(`${API}/machine-data/import-csv`, {
          rows,
          apply_to_miners: apply
        });
        const d = res.data;
        toast.success(
          `Updated ${d.db_updated} DB row(s)` +
          (apply ? ` · queued ${d.commands_queued} miner command(s)` : "") +
          (d.skipped_count ? ` · skipped ${d.skipped_count}` : "")
        );
        if (d.skipped_count > 0) {
          console.log("Import skipped rows:", d.skipped);
        }
        fetchLiveMachines();
      } catch (err) {
        toast.error(`Import failed: ${err?.response?.data?.detail || err.message}`);
      }
    };
    input.click();
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (e) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchMachineMonitor = async (forceRefresh = false) => {
    try {
      const qs = forceRefresh ? '&force_refresh=true' : '';
      
      if (monitorMode === "api") {
        // Try API-based endpoint first
        try {
          const res = await axios.get(`${API}/admin/machine-monitor?mode=api${qs}`);
          if (res.data.success) {
            setMachineMonitor(res.data);
            return;
          }
        } catch (e) { /* fall through */ }
        
        // API failed — auto-fallback to watcher
        const res = await axios.get(`${API}/admin/machine-monitor?mode=watcher${qs}`);
        setMachineMonitor(res.data);
      } else {
        // Watcher mode directly
        const res = await axios.get(`${API}/admin/machine-monitor?mode=watcher${qs}`);
        setMachineMonitor(res.data);
      }
    } catch (e) {
      console.error("Failed to load machine monitor");
    } finally {
      setMonitorLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefreshMonitor = () => {
    setRefreshing(true);
    fetchMachineMonitor(true);  // Force refresh when clicking the button
  };

  const handleHideWorker = async (detail) => {
    const label = `${detail.machine_name || detail.worker} (${detail.coin})`;
    if (!window.confirm(`Hide stale worker "${label}" from the dashboard?\n\nThis only hides the entry — it does NOT touch the miner. ViaBTC auto-purges old workers after ~7 days.`)) {
      return;
    }
    try {
      await axios.post(`${API}/viabtc/hide-worker`, {
        account: detail.worker || "",
        machine_name: detail.machine_name || "",
        coin: detail.coin || ""
      });
      toast.success(`Hidden: ${label}`);
      fetchMachineMonitor(true);
    } catch (e) {
      toast.error(`Failed to hide worker: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleKeepWorker = async (detail) => {
    const label = `${detail.machine_name || detail.worker} (${detail.coin})`;
    try {
      await axios.post(`${API}/machine-data/keep-offline`, {
        worker: detail.worker || "",
        worker_name: detail.worker_name || "",
        machine_name: detail.machine_name || "",
        coin: detail.coin || "",
        reason: detail.reason || "offline",
        last_active: detail.last_active || 0,
        ip: detail.ip || "",
        model: detail.model || "",
        farm: detail.farm || ""
      });
      toast.success(`Kept: ${label} — stays listed until it's back online or you remove it`);
      fetchMachineMonitor(true);
    } catch (e) {
      toast.error(`Failed to keep worker: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleUnkeepWorker = async (detail) => {
    const label = `${detail.machine_name || detail.worker} (${detail.coin})`;
    if (!window.confirm(`Remove kept machine "${label}" from the dashboard?`)) {
      return;
    }
    try {
      await axios.post(`${API}/machine-data/unkeep-offline`, {
        worker: detail.worker || "",
        machine_name: detail.machine_name || "",
        coin: detail.coin || ""
      });
      toast.success(`Removed: ${label}`);
      fetchMachineMonitor(true);
    } catch (e) {
      toast.error(`Failed to remove kept worker: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleRepairPin = async ({ ip, worker_name }) => {
    const label = worker_name || ip;
    try {
      await axios.post(`${API}/machine-data/repair-pin`, { ip: ip || "", worker_name: worker_name || "" });
      toast.success(`${label} marked as in repair — clears itself when it's back online`);
      fetchLiveMachines();
      fetchMachineMonitor(true);
    } catch (e) {
      toast.error(`Failed to mark repair: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleRepairUnpin = async ({ ip, worker_name }) => {
    const label = worker_name || ip;
    if (!window.confirm(`Remove the "in repair" mark from ${label}?`)) return;
    try {
      await axios.post(`${API}/machine-data/repair-unpin`, { ip: ip || "", worker_name: worker_name || "" });
      toast.success(`Repair mark removed from ${label}`);
      fetchLiveMachines();
      fetchMachineMonitor(true);
    } catch (e) {
      toast.error(`Failed to remove repair mark: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-32 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="skeleton h-80 rounded-xl" />
          <div className="skeleton h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className={`stat-card ${color}`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-gray-400 mb-1 truncate">{title}</p>
          <h3 className="text-lg sm:text-2xl font-bold text-white truncate">{value}</h3>
          {subtitle && (
            <p className="text-[11px] sm:text-xs text-gray-500 mt-1 truncate">{subtitle}</p>
          )}
        </div>
        <div className={`p-2 sm:p-3 rounded-lg shrink-0 ${
          color === 'profit' ? 'bg-[#00E054]/10 text-[#00E054]' :
          color === 'cost' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
          color === 'revenue' ? 'bg-[#00C2FF]/10 text-[#00C2FF]' :
          'bg-[#7C3AED]/10 text-[#7C3AED]'
        }`}>
          <Icon size={20} className="sm:hidden" />
          <Icon size={24} className="hidden sm:block" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${
          trend >= 0 ? 'text-[#00E054]' : 'text-[#EF4444]'
        }`}>
          {trend >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
          <span>{Math.abs(trend).toFixed(1)}% margin</span>
        </div>
      )}
    </div>
  );

  // Prepare chart data
  const monthlyData = Object.entries(stats?.monthly_stats || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6)
    .map(([month, data]) => ({
      month: month.split('-')[1] || month,
      paid: data.paid || 0,
      unpaid: data.unpaid || 0,
      paused: data.paused || 0,
    }));

  const machineData = Object.entries(stats?.machine_counts || {}).map(([name, count]) => ({
    name,
    value: count
  }));

  const COLORS = ['#00E054', '#7C3AED', '#00C2FF', '#EAB308', '#EF4444', '#F97316'];

  // Mirrors the backend's normalization in /dashboard/stats (machine_counts):
  // variants like L9-250/L9-260/L9-275 collapse into the same "L9" bucket.
  const normalizeMachineName = (name) => (name || "").toUpperCase().replace(/[-_]\d+$/, "");

  const handleShowTypeBreakdown = async (baseName) => {
    setTypeBreakdown({ name: baseName, loading: true, rows: [], byPrice: [] });
    try {
      const [customersRes, typesRes, groupsRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/machine-types`),
        axios.get(`${API}/machine-type-groups`),
      ]);
      const typeById = {};
      for (const t of typesRes.data || []) typeById[t.id] = t;
      // Mirrors the backend's grouping in /dashboard/stats: an admin-defined
      // group (e.g. "L9 200" + "L9 275" -> "L9") takes priority; anything not
      // in a group falls back to the automatic hyphen-suffix normalization.
      const typeIdToGroupName = {};
      for (const g of groupsRes.data || []) {
        for (const tid of g.member_type_ids || []) typeIdToGroupName[tid] = g.group_name;
      }
      const bucketFor = (m) => typeIdToGroupName[m.machine_type_id] || normalizeMachineName(m.machine_name);

      const rows = [];
      const priceTally = {}; // fee -> quantity, across every matching variant/customer
      let pausedQty = 0;
      let activeQty = 0;
      for (const c of customersRes.data || []) {
        for (const m of c.machines || []) {
          if (bucketFor(m) !== baseName) continue;
          const qty = m.quantity || 1;
          const fee = typeById[m.machine_type_id]?.monthly_fee;
          const status = c.status || "active";
          rows.push({
            customer_name: c.name,
            machine_name: m.machine_name,
            quantity: qty,
            fee,
            status,
          });
          const feeKey = fee === undefined || fee === null ? "unknown" : fee;
          priceTally[feeKey] = (priceTally[feeKey] || 0) + qty;
          if (status === "paused") pausedQty += qty; else activeQty += qty;
        }
      }
      rows.sort((a, b) => b.quantity - a.quantity);
      const byPrice = Object.entries(priceTally)
        .map(([fee, qty]) => ({ fee, qty }))
        .sort((a, b) => (b.fee === "unknown" ? -1 : +b.fee) - (a.fee === "unknown" ? -1 : +a.fee));
      setTypeBreakdown({ name: baseName, loading: false, rows, byPrice, pausedQty, activeQty });
    } catch (e) {
      setTypeBreakdown({ name: baseName, loading: false, rows: [], byPrice: [], error: true });
    }
  };

  const fetchGroupManagementData = async () => {
    try {
      const [typesRes, groupsRes] = await Promise.all([
        axios.get(`${API}/machine-types`),
        axios.get(`${API}/machine-type-groups`),
      ]);
      setAllMachineTypes(typesRes.data || []);
      setMachineTypeGroups(groupsRes.data || []);
    } catch (e) {
      toast.error("Failed to load machine types/groups");
    }
  };

  const handleOpenManageGroups = () => {
    setNewGroupName("");
    setNewGroupSelectedIds([]);
    setEditingGroupId(null);
    setManageGroupsOpen(true);
    fetchGroupManagementData();
  };

  const toggleNewGroupType = (typeId) => {
    setNewGroupSelectedIds(prev =>
      prev.includes(typeId) ? prev.filter(id => id !== typeId) : [...prev, typeId]
    );
  };

  const handleCreateGroup = async () => {
    if (newGroupSelectedIds.length < 2) {
      toast.error("Pick at least 2 machine types to combine");
      return;
    }
    if (!newGroupName.trim()) {
      toast.error("Give the combined group a name");
      return;
    }
    try {
      await axios.post(`${API}/machine-type-groups`, {
        group_name: newGroupName.trim(),
        member_type_ids: newGroupSelectedIds,
      });
      toast.success("Group created");
      setNewGroupName("");
      setNewGroupSelectedIds([]);
      await fetchGroupManagementData();
      fetchStats();
    } catch (e) {
      toast.error("Failed to create group");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Remove this machine type group? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/machine-type-groups/${groupId}`);
      toast.success("Group removed");
      await fetchGroupManagementData();
      fetchStats();
    } catch (e) {
      toast.error("Failed to remove group");
    }
  };

  const handleStartEditGroup = (group) => {
    setEditingGroupId(group.id);
    setEditGroupName(group.group_name);
    setEditGroupSelectedIds(group.member_type_ids || []);
  };

  const toggleEditGroupType = (typeId) => {
    setEditGroupSelectedIds(prev =>
      prev.includes(typeId) ? prev.filter(id => id !== typeId) : [...prev, typeId]
    );
  };

  const handleSaveGroupEdit = async () => {
    if (editGroupSelectedIds.length < 2) {
      toast.error("A group needs at least 2 machine types");
      return;
    }
    if (!editGroupName.trim()) {
      toast.error("Give the combined group a name");
      return;
    }
    try {
      await axios.put(`${API}/machine-type-groups/${editingGroupId}`, {
        group_name: editGroupName.trim(),
        member_type_ids: editGroupSelectedIds,
      });
      toast.success("Group updated");
      setEditingGroupId(null);
      await fetchGroupManagementData();
      fetchStats();
    } catch (e) {
      toast.error("Failed to update group");
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
      <div className="page-header">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Your crypto farm overview</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          title="Monthly Profit"
          value={`$${(stats?.monthly_profit || 0).toLocaleString()}`}
          subtitle="Revenue - Costs"
          icon={TrendingUp}
          color="profit"
          trend={stats?.profit_margin}
        />
        <StatCard
          title="Monthly Revenue"
          value={`$${(stats?.total_monthly_revenue || 0).toLocaleString()}`}
          subtitle="From active customers"
          icon={DollarSign}
          color="revenue"
        />
        <StatCard
          title="Monthly Cost"
          value={`$${(stats?.total_monthly_cost || 0).toLocaleString()}`}
          subtitle="Your expenses"
          icon={AlertCircle}
          color="cost"
        />
        <StatCard
          title="Active Customers"
          value={stats?.active_customers || 0}
          subtitle={`${stats?.paused_customers || 0} paused`}
          icon={Users}
          color="secondary"
        />
      </div>

      {/* Overdue Payments */}
      {overdueData && overdueData.total_customers > 0 && (
        <div className="bg-gradient-to-r from-red-500/5 to-[#0F0F0F] rounded-xl border border-red-500/30 p-4 sm:p-6" data-testid="overdue-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-red-500/10 shrink-0">
                <AlertTriangle className="text-red-500" size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-white">Overdue Payments</h2>
                <p className="text-sm text-gray-500">{overdueData.total_customers} customer(s) with past-due balance</p>
              </div>
            </div>
            <div className="sm:text-right shrink-0">
              <p className="text-2xl font-bold text-red-400">${overdueData.total_overdue.toLocaleString()}</p>
              <p className="text-xs text-gray-500">total overdue</p>
            </div>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {[...overdueData.overdue_customers].sort((a,b) => (a.customer_name||'').localeCompare(b.customer_name||'', undefined, {sensitivity:'base'})).map((cust, idx) => (
              <div key={idx} className="bg-[#0A0A0A] rounded-lg border border-[#27272A] p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-white">{cust.customer_name}</span>
                    {cust.status === 'paused' && <span className="text-xs text-yellow-500 ml-2">(paused)</span>}
                  </div>
                  <span className="text-lg font-bold text-red-400">${cust.total_owed.toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {cust.months.map((m, mi) => (
                    <span key={mi} className="text-xs bg-red-500/10 text-red-400 px-2 py-1 rounded">
                      {m.month}: ${m.amount.toLocaleString()}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customers Online (admin-only) */}
      <div className="bg-gradient-to-r from-[#00E054]/5 to-[#0F0F0F] rounded-xl border border-[#00E054]/30 p-4 sm:p-6" data-testid="online-customers-section">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-[#00E054]/10 shrink-0">
              <Users className="text-[#00E054]" size={20} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white">Customers Online</h2>
              <p className="text-sm text-gray-500">Only you can see who is signed in to the portal</p>
            </div>
          </div>
          <div className="sm:text-right shrink-0">
            <p className="text-2xl font-bold text-[#00E054]">
              {onlineTab === "now" ? onlineCustomers.length : recentOnline.length}
            </p>
            <p className="text-xs text-gray-500">{onlineTab === "now" ? "online now" : "active in 24h"}</p>
          </div>
        </div>

        <div className="flex gap-1 mb-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg p-1 w-fit">
          <button
            onClick={() => setOnlineTab("now")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              onlineTab === "now" ? "bg-[#00E054] text-black" : "text-gray-400 hover:text-white"
            }`}
          >
            Online Now
          </button>
          <button
            onClick={() => { setOnlineTab("24h"); if (recentOnline.length === 0) fetchRecentOnline(); }}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              onlineTab === "24h" ? "bg-[#00E054] text-black" : "text-gray-400 hover:text-white"
            }`}
          >
            Past 24h
          </button>
        </div>

        {onlineTab === "now" ? (
          onlineCustomers.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">No customers are signed in right now.</p>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {onlineCustomers.map((c) => (
                <div key={c.session_id} className="bg-[#0A0A0A] rounded-lg border border-[#27272A] p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-[#00E054] flex-shrink-0 animate-pulse" />
                    <div className="min-w-0">
                      <button
                        className="text-sm font-medium text-white truncate hover:underline text-left"
                        onClick={() => openSessionHistory(c.customer_id, c.name)}
                        title="View login history"
                      >
                        {c.name || "Unknown"}
                      </button>
                      {c.phone && <p className="text-xs text-gray-500 truncate">{c.phone}</p>}
                      <p className="text-xs text-gray-500 truncate">
                        {parseUserAgent(c.user_agent)}{c.ip ? ` · ${c.ip}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-[#00E054]">{formatOnlineDuration(c.login_at, c.last_seen)}</p>
                    <p className="text-xs text-gray-500">online time</p>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : recentOnlineLoading ? (
          <p className="text-sm text-gray-500 py-2">Loading…</p>
        ) : recentOnline.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No customer activity in the past 24 hours.</p>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {recentOnline.map((c) => (
              <div key={c.customer_id} className="bg-[#0A0A0A] rounded-lg border border-[#27272A] p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <button
                    className="text-sm font-medium text-white truncate hover:underline text-left"
                    onClick={() => openSessionHistory(c.customer_id, c.name)}
                    title="View login history"
                  >
                    {c.name || "Unknown"}
                  </button>
                  {c.phone && <p className="text-xs text-gray-500 truncate">{c.phone}</p>}
                  <p className="text-xs text-gray-500 truncate">
                    {c.sessions} session{c.sessions === 1 ? "" : "s"}
                    {c.device_count > 1 ? ` · ${c.device_count} devices` : ""}
                    {c.ips.length ? ` · ${c.ips[0]}${c.ips.length > 1 ? ` +${c.ips.length - 1}` : ""}` : ""}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-[#00E054]">
                    {c.last_seen ? new Date(c.last_seen).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                  </p>
                  <p className="text-xs text-gray-500">last seen</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer login history modal */}
      {sessionHistory && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl max-w-xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#27272A] p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">Login history — {sessionHistory.name || "Unknown"}</h2>
              <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => setSessionHistory(null)}>✕</Button>
            </div>
            <div className="p-4">
              {sessionHistoryLoading ? (
                <p className="text-center py-8 text-gray-500">Loading…</p>
              ) : sessionHistory.sessions.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No login history yet.</p>
              ) : (
                <div className="space-y-2">
                  {sessionHistory.sessions.map((s) => (
                    <div key={s.session_id} className="bg-[#18181B] border border-[#27272A] rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {s.is_active && <span className="w-1.5 h-1.5 rounded-full bg-[#00E054] shrink-0 animate-pulse" />}
                          <p className="text-sm text-white truncate">{parseUserAgent(s.user_agent)}</p>
                        </div>
                        <p className="text-xs text-gray-400 shrink-0">
                          {s.is_active ? "Online now" : `Online ${Math.floor(s.duration_seconds / 60)}m`}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <p className="text-xs text-gray-500 font-mono">{s.ip || "—"}</p>
                        <p className="text-xs text-gray-500">{s.login_at ? new Date(s.login_at).toLocaleString() : "—"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Offline/Crashed machines modal */}
      {showOfflineModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#27272A] p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">
                Offline / Crashed ({liveMachines.filter(m => m.status !== 'online').length})
              </h2>
              <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => setShowOfflineModal(false)}>✕</Button>
            </div>
            <div className="p-4">
              {liveMachines.filter(m => m.status !== 'online').length === 0 ? (
                <p className="text-center py-8 text-gray-500">Nothing offline right now ✅</p>
              ) : (
                <div className="space-y-2">
                  {liveMachines
                    .filter(m => m.status !== 'online')
                    .sort((a, b) => (b.seconds_since_update ?? 0) - (a.seconds_since_update ?? 0))
                    .map((m) => (
                      <div key={m.ip} className="bg-[#18181B] border border-[#27272A] rounded-lg p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">
                            {m.worker_name || "—"}
                            {m.is_customer && <span className="ml-1.5 text-[10px] bg-[#00E054]/20 text-[#00E054] px-1 rounded">customer</span>}
                          </p>
                          <p className="text-xs text-gray-500 font-mono truncate">{m.ip} · {m.farm || "—"}</p>
                        </div>
                        <p className="text-xs text-red-400 shrink-0">{formatDurationAgo(m.seconds_since_update)}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Real-Time Machine Monitor */}
      <div className="bg-gradient-to-r from-[#0F0F0F] to-[#1A1A1A] rounded-xl border border-[#27272A] p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-[#00E054]/10 shrink-0">
              <Server className="text-[#00E054]" size={24} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white">Real-Time Machine Monitor</h2>
              <p className="text-sm text-gray-500">
                {machineMonitor?.mode === "watcher" ? "Using watcher links" : machineMonitor?.mode === "api" ? "Using API keys" : "Live worker status from ViaBTC"}
              </p>

            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-[#1A1A1A] border border-[#27272A] rounded-lg overflow-hidden">
              <button
                onClick={() => { setMonitorMode("api"); setMonitorLoading(true); }}
                className={`px-3 py-2 sm:py-1.5 text-xs font-medium transition-colors ${
                  monitorMode === "api"
                    ? "bg-[#00E054] text-black"
                    : "text-gray-400 hover:text-white"
                }`}
                data-testid="monitor-mode-api"
              >
                API
              </button>
              <button
                onClick={() => { setMonitorMode("watcher"); setMonitorLoading(true); }}
                className={`px-3 py-2 sm:py-1.5 text-xs font-medium transition-colors ${
                  monitorMode === "watcher"
                    ? "bg-[#00E054] text-black"
                    : "text-gray-400 hover:text-white"
                }`}
                data-testid="monitor-mode-watcher"
              >
                Watcher
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshMonitor}
              disabled={refreshing}
              className="border-[#27272A] hover:bg-[#27272A] flex-1 sm:flex-none"
              data-testid="monitor-refresh-btn"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {monitorLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="skeleton h-32 rounded-xl" />)}
          </div>
        ) : machineMonitor?.success ? (
          <>
            {/* By pool: F2Pool together, ViaBTC together */}
            {(() => {
              const st = machineMonitor.stats || {};
              const vOn = (st.ltc?.online || 0) + (st.kas?.online || 0) + (st.zec?.online || 0);
              const vOff = (st.ltc?.offline || 0) + (st.kas?.offline || 0) + (st.zec?.offline || 0);
              const vTot = (st.ltc?.total || 0) + (st.kas?.total || 0) + (st.zec?.total || 0);
              const f = f2poolBreakdown?.enabled ? f2poolBreakdown : null;
              const PoolCard = ({ label, color, on, off, tot, note }) => (
                <div className="bg-[#0A0A0A] rounded-xl p-4 border" style={{ borderColor: color + "55" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                    <span className="font-bold" style={{ color }}>{label}</span>
                    {note && <span className="text-xs text-gray-500">{note}</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-[#00E054]"><Wifi size={16} /><span className="text-2xl font-bold">{on}</span></div>
                      <p className="text-xs text-gray-500">Online</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-[#EF4444]"><WifiOff size={16} /><span className="text-2xl font-bold">{off}</span></div>
                      <p className="text-xs text-gray-500">Offline</p>
                    </div>
                    <div className="text-center">
                      <span className="text-2xl font-bold text-white">{tot}</span>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                  </div>
                </div>
              );
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <PoolCard label="ViaBTC" color="#00C2FF" on={vOn} off={vOff} tot={vTot} />
                  {f2poolBreakdown?.warming
                    ? <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#F59E0B]/40 flex items-center justify-center text-xs text-[#F59E0B] text-center">{f2poolBreakdown.error
                        ? `F2Pool: ${f2poolBreakdown.error}`
                        : f2poolBreakdown.progress
                          ? `F2Pool: scanning accounts — ${f2poolBreakdown.progress.done} of ${f2poolBreakdown.progress.total} checked (${f2poolBreakdown.progress.coin})…`
                          : "F2Pool: loading machine list…"}</div>
                    : f
                    ? <div className="bg-[#0A0A0A] rounded-xl p-4 border" style={{ borderColor: "#F59E0B55" }}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#F59E0B" }} />
                          <span className="font-bold" style={{ color: "#F59E0B" }}>F2Pool</span>
                          <span className="text-[11px] text-gray-500">click Online/Offline to see names</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <button className={`text-center rounded-lg px-2 py-1 ${f2poolShow === "online" ? "bg-[#00E054]/10" : "hover:bg-white/5"}`} onClick={() => openF2poolList("online")}>
                            <div className="flex items-center gap-1 text-[#00E054]"><Wifi size={16} /><span className="text-2xl font-bold">{f.online}</span></div>
                            <p className="text-xs text-gray-500">Online</p>
                          </button>
                          <button className={`text-center rounded-lg px-2 py-1 ${f2poolShow === "offline" ? "bg-[#EF4444]/10" : "hover:bg-white/5"}`} onClick={() => openF2poolList("offline")}>
                            <div className="flex items-center gap-1 text-[#EF4444]"><WifiOff size={16} /><span className="text-2xl font-bold">{f.offline}</span></div>
                            <p className="text-xs text-gray-500">Offline</p>
                          </button>
                          <div className="text-center px-2">
                            <span className="text-2xl font-bold text-white">{f.total}</span>
                            <p className="text-xs text-gray-500">Total</p>
                          </div>
                        </div>
                      </div>
                    : <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#27272A] flex items-center justify-center text-xs text-gray-500 text-center">
                        F2Pool not enabled — turn it on in Machines &amp; Repairs → ViaBTC API tab → F2Pool API.
                      </div>}
                </div>
              );
            })()}
            {f2poolShow && (() => {
              const list = f2poolWorkers.filter(w => (f2poolShow === "online" ? w.online : !w.online));
              return (
                <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#F59E0B]/30 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm" style={{ color: f2poolShow === "online" ? "#00E054" : "#EF4444" }}>
                      F2Pool — {f2poolShow} ({list.length})
                    </span>
                    <button className="text-xs text-gray-500 hover:text-white" onClick={() => setF2poolShow(null)}>close ✕</button>
                  </div>
                  {list.length === 0 ? <p className="text-xs text-gray-500">None.</p> : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                      {list.map((w, i) => (
                        <div key={i} className="text-xs bg-[#141414] border border-[#27272A] rounded px-2 py-1.5 flex items-center justify-between gap-2">
                          <span className="text-white truncate">{w.name}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-gray-500">{w.coin}{w.online ? ` · ${w.hr || (w.h1_gh + "GH")}` : ""}</span>
                            {!w.online && (
                              <button
                                title="Hide this offline worker from the dashboard"
                                className="text-gray-600 hover:text-[#EF4444] transition-colors"
                                onClick={() => hideF2poolWorker(w)}
                              >✕</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Machine Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* LTC / KAS / ZEC — ViaBTC + F2Pool combined; click Online/Offline for names by pool */}
              {[
                { key: "ltc", coin: "LTC", label: "LTC Miners", icon: "💎", titleCls: "text-gray-300", border: "border-gray-700/50" },
                { key: "kas", coin: "KAS", label: "KAS Miners", icon: "🟢", titleCls: "text-teal-300", border: "border-teal-700/50" },
                { key: "zec", coin: "ZEC", label: "ZEC Miners", icon: "🔷", titleCls: "text-[#00C2FF]", border: "border-[#00C2FF]/40" },
              ].map(c => {
                const v = machineMonitor.stats?.[c.key] || {};
                const f = (f2poolBreakdown?.enabled && f2poolBreakdown.by_coin?.[c.coin]) || { online: 0, offline: 0, total: 0 };
                const active = coinShow?.coin === c.coin ? coinShow.status : null;
                return (
                  <div key={c.key} className={`bg-[#0A0A0A] rounded-xl p-4 border ${c.border}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{c.icon}</span>
                      <span className={`font-bold ${c.titleCls}`}>{c.label}</span>
                      {v.not_synced > 0 && (
                        <span className="text-xs text-gray-500">({v.not_synced} not synced)</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        className={`text-center rounded-lg px-2 py-1 transition-colors ${active === "online" ? "bg-[#00E054]/10" : "hover:bg-white/5"}`}
                        onClick={() => openCoinList(c.coin, "online")}
                        title="Click to see who is online, by pool"
                      >
                        <div className="flex items-center gap-1 text-[#00E054]">
                          <Wifi size={16} />
                          <span className="text-2xl font-bold">{(v.online || 0) + f.online}</span>
                        </div>
                        <p className="text-xs text-gray-500">Online</p>
                      </button>
                      <button
                        className={`text-center rounded-lg px-2 py-1 transition-colors ${active === "offline" ? "bg-[#EF4444]/10" : "hover:bg-white/5"}`}
                        onClick={() => openCoinList(c.coin, "offline")}
                        title="Click to see who is offline, by pool"
                      >
                        <div className="flex items-center gap-1 text-[#EF4444]">
                          <WifiOff size={16} />
                          <span className="text-2xl font-bold">{(v.offline || 0) + f.offline}</span>
                        </div>
                        <p className="text-xs text-gray-500">Offline</p>
                      </button>
                      <div className="text-center px-2">
                        <span className="text-2xl font-bold text-white">{(v.total || 0) + f.total}</span>
                        <p className="text-xs text-gray-500">Total</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">
                      <span className="text-[#00C2FF]">ViaBTC</span> {v.online || 0}/{v.total || 0}
                      <span className="mx-1.5">·</span>
                      <span className="text-[#F59E0B]">F2Pool</span> {f.online}/{f.total}
                    </p>
                  </div>
                );
              })}

              {/* Total */}
              <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#7C3AED]/50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">⚡</span>
                  <span className="font-bold text-[#7C3AED]">All Miners</span>
                  {machineMonitor.stats.total.not_synced > 0 && (
                    <span className="text-xs text-gray-500">({machineMonitor.stats.total.not_synced} not synced)</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div
                    className="text-center cursor-pointer hover:bg-[#00E054]/10 rounded-lg p-2 transition-colors"
                    onClick={() => { const next = !showOnline; setShowOnline(next); if (next && f2poolWorkers.length === 0) { axios.get(`${API}/admin/f2pool-workers`).then(r => setF2poolWorkers(r.data?.workers || [])).catch(() => {}); } }}
                    title="Click to view online machines"
                  >
                    <div className="flex items-center gap-1 text-[#00E054]">
                      <Wifi size={16} />
                      <span className="text-2xl font-bold">{machineMonitor.stats.total.online + (f2poolBreakdown?.enabled ? (f2poolBreakdown.online || 0) : 0)}</span>
                      {showOnline ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                    <p className="text-xs text-gray-500">Online</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-[#EF4444]">
                      <WifiOff size={16} />
                      <span className="text-2xl font-bold">{machineMonitor.stats.total.offline + (f2poolBreakdown?.enabled ? (f2poolBreakdown.offline || 0) : 0)}</span>
                    </div>
                    <p className="text-xs text-gray-500">Offline</p>
                  </div>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-white">{machineMonitor.stats.total.total + (f2poolBreakdown?.enabled ? (f2poolBreakdown.total || 0) : 0)}</span>
                    <p className="text-xs text-gray-500">Synced</p>
                  </div>
                </div>
              </div>
            </div>

            {coinShow && (() => {
              const { coin, status } = coinShow;
              const online = status === "online";
              const via = online
                ? (machineMonitor.online_details || []).filter(d => d.coin === coin).flatMap(d =>
                    d.workers?.length
                      ? d.workers.map(w => ({ name: w.name, sub: d.worker, right: formatHashrate(w.hashrate, coin) }))
                      : [{ name: d.worker, sub: `${d.machines} machine(s)`, right: formatHashrate(d.hashrate, coin) }])
                : (machineMonitor.offline_details || []).filter(d => d.coin === coin).map(d => ({
                    name: d.machine_name || d.worker, sub: d.machine_name ? d.worker : "", right: d.ip || "" }));
              const f2 = f2poolBreakdown?.enabled
                ? f2poolWorkers.filter(w => w.coin === coin && w.online === online).map(w => ({
                    name: w.name, sub: w.account && w.account !== w.name ? w.account : "", right: online ? w.hr : "", raw: w }))
                : [];
              const section = (title, color, items) => (
                <div className="mb-3 last:mb-0">
                  <div className="text-xs font-bold mb-1.5" style={{ color }}>{title} ({items.length})</div>
                  {items.length === 0 ? <p className="text-xs text-gray-600">None.</p> : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {items.map((it, i) => (
                        <div key={i} className="text-xs bg-[#141414] border border-[#27272A] rounded px-2 py-1.5 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <span className="text-white truncate block">{it.name}</span>
                            {it.sub && <span className="text-gray-500 truncate block">{it.sub}</span>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {it.right && <span className={online ? "text-yellow-400" : "text-cyan-400 font-mono"}>{it.right}</span>}
                            {it.raw && !online && (
                              <button
                                title="Hide this offline worker from the dashboard"
                                className="text-gray-600 hover:text-[#EF4444] transition-colors"
                                onClick={() => hideF2poolWorker(it.raw)}
                              >✕</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
              return (
                <div className={`bg-[#0A0A0A] rounded-xl p-4 border mb-6 ${online ? "border-[#00E054]/30" : "border-[#EF4444]/30"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold text-sm" style={{ color: online ? "#00E054" : "#EF4444" }}>
                      {coin} — {status} ({via.length + f2.length})
                    </span>
                    <button className="text-xs text-gray-500 hover:text-white" onClick={() => setCoinShow(null)}>close ✕</button>
                  </div>
                  {section("ViaBTC", "#00C2FF", via)}
                  {f2poolBreakdown?.enabled && section("F2Pool", "#F59E0B", f2)}
                </div>
              );
            })()}

            {/* Online Workers Details (Expandable) */}
            {showOnline && (machineMonitor.online_details?.length > 0 || (f2poolBreakdown?.enabled && f2poolWorkers.some(w => w.online))) && (
              <div className="bg-[#00E054]/10 border border-[#00E054]/30 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Wifi className="text-[#00E054]" size={20} />
                  <span className="font-bold text-[#00E054]">
                    Online Machines ({(machineMonitor.online_details || []).reduce((sum, d) => sum + d.machines, 0) + (f2poolBreakdown?.enabled ? f2poolWorkers.filter(w => w.online).length : 0)})
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {(machineMonitor.online_details || []).map((detail, idx) => {
                    const accountKey = `${detail.worker_name}-${detail.coin}`;
                    const isExpanded = expandedAccount === accountKey;
                    
                    return (
                      <div key={idx} className="bg-[#0A0A0A] rounded-lg overflow-hidden">
                        {/* Account Header - Clickable */}
                        <div 
                          className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-[#1A1A1A] transition-colors"
                          onClick={() => setExpandedAccount(isExpanded ? null : accountKey)}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Wifi size={14} className="text-[#00E054] flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm text-white font-medium truncate block">{detail.worker}</span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-[#00E054]">({detail.machines})</span>
                                <span className="text-yellow-400">{formatHashrate(detail.hashrate, detail.coin)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              detail.coin === 'LTC' ? 'bg-gray-700 text-gray-300' : detail.coin === 'ZEC' ? 'bg-sky-900 text-sky-300' : 'bg-teal-900 text-teal-300'
                            }`}>{detail.coin}</span>
                            {detail.workers?.length > 0 && (
                              isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Workers List */}
                        {isExpanded && detail.workers?.length > 0 && (
                          <div className="border-t border-gray-800 bg-[#050505] px-2 py-2 max-h-48 overflow-y-auto">
                            {detail.workers.map((w, wIdx) => (
                              <div key={wIdx} className="flex items-center justify-between py-1 px-2 text-xs hover:bg-[#0A0A0A] rounded">
                                <span className="text-gray-300 truncate">{w.name}</span>
                                <span className="text-yellow-400 ml-2 flex-shrink-0">{formatHashrate(w.hashrate, detail.coin)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* F2Pool online workers (from the F2Pool pool API, not the local sync) */}
                  {(f2poolBreakdown?.enabled ? f2poolWorkers.filter(w => w.online) : []).map((w, idx) => (
                    <div key={`f2p-${idx}`} className="bg-[#0A0A0A] rounded-lg overflow-hidden">
                      <div className="px-3 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Wifi size={14} className="text-[#00E054] flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="text-sm text-white font-medium truncate block">{w.name}</span>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-yellow-400">{w.hr || (w.h1_gh + " GH/s")}</span>
                              <span className="text-[#F59E0B]">F2Pool</span>
                            </div>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                          w.coin === 'LTC' ? 'bg-gray-700 text-gray-300' : w.coin === 'ZEC' ? 'bg-sky-900 text-sky-300' : 'bg-teal-900 text-teal-300'
                        }`}>{w.coin}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Offline Workers Alert */}
            {machineMonitor.offline_details?.length > 0 && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="text-[#EF4444]" size={20} />
                  <span className="font-bold text-[#EF4444]">
                    Offline Machines ({machineMonitor.offline_details.length})
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {machineMonitor.offline_details.map((detail, idx) => {
                    const offlineDuration = detail.last_active ? (() => {
                      const seconds = Math.floor(Date.now() / 1000) - detail.last_active;
                      if (seconds < 60) return `${seconds}s ago`;
                      const minutes = Math.floor(seconds / 60);
                      if (minutes < 60) return `${minutes}m ago`;
                      const hours = Math.floor(minutes / 60);
                      if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
                      const days = Math.floor(hours / 24);
                      return `${days}d ${hours % 24}h ago`;
                    })() : '';
                    return (
                    <div key={idx} className="bg-[#0A0A0A] rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <WifiOff size={14} className="text-[#EF4444] shrink-0" />
                        <div className="min-w-0">
                          <span className="text-sm text-white font-medium">{detail.machine_name || detail.worker}</span>
                          <span className="text-xs text-gray-500 ml-2">({detail.worker})</span>
                          {detail.ip && (
                            <span className="text-xs text-cyan-400 ml-2 font-mono">{detail.ip}</span>
                          )}
                          {(detail.model || detail.farm) && (
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              {detail.model && <span>{detail.model}</span>}
                              {detail.model && detail.farm && <span className="mx-1">·</span>}
                              {detail.farm && <span className="text-purple-400">{detail.farm}</span>}
                            </div>
                          )}
                          {offlineDuration && (
                            <div className="text-xs text-[#EF4444]/80 mt-0.5">Offline since {offlineDuration}</div>
                          )}
                          {detail.is_kept && (
                            <div className="text-[10px] text-amber-400 mt-0.5 flex items-center gap-1">
                              <Pin size={10} /> Kept
                            </div>
                          )}
                          {detail.in_repair && (
                            <div className="text-[10px] text-orange-400 mt-0.5 flex items-center gap-1"
                                 title="Marked as sent to repair — clears automatically when it's back online">
                              <Wrench size={10} /> In repair
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          detail.coin === 'LTC' ? 'bg-gray-700 text-gray-300' : 'bg-teal-900 text-teal-300'
                        }`}>{detail.coin}</span>
                        {detail.is_kept ? (
                          <button
                            onClick={() => handleUnkeepWorker(detail)}
                            title="Remove this kept machine"
                            aria-label="Remove kept machine"
                            className="p-1 rounded hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 transition-colors"
                            data-testid={`unkeep-offline-worker-${idx}`}
                          >
                            <PinOff size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleKeepWorker(detail)}
                            title="Keep this machine listed even if it drops out of ViaBTC (sold / permanently off)"
                            aria-label="Keep offline machine"
                            className="p-1 rounded hover:bg-amber-500/20 text-gray-500 hover:text-amber-400 transition-colors"
                            data-testid={`keep-offline-worker-${idx}`}
                          >
                            <Pin size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => detail.in_repair
                            ? handleRepairUnpin({ ip: detail.ip, worker_name: detail.machine_name })
                            : handleRepairPin({ ip: detail.ip, worker_name: detail.machine_name })}
                          title={detail.in_repair
                            ? "Remove the in-repair mark"
                            : "Mark as sent to repair — clears automatically when it's back online"}
                          aria-label={detail.in_repair ? "Remove repair mark" : "Mark as in repair"}
                          className={`p-1 rounded hover:bg-orange-500/20 transition-colors ${
                            detail.in_repair ? "text-orange-400 hover:text-orange-300" : "text-gray-500 hover:text-orange-400"
                          }`}
                          data-testid={`repair-offline-worker-${idx}`}
                        >
                          <Wrench size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const params = new URLSearchParams();
                              if (detail.worker) params.set("worker", detail.worker);
                              if (detail.ip) params.set("ip", detail.ip);
                              const r = await axios.get(`${API}/machine-data/why-no-alert?${params.toString()}`);
                              const reasons = (r.data?.reasons || []).map((s, i) => `${i + 1}. ${s}`).join("\n\n");
                              const info = r.data?.info || {};
                              const extra = [
                                info.live_status ? `Dashboard status: ${info.live_status}` : "",
                                info.viabtc_status ? `ViaBTC pool status: ${info.viabtc_status}` : "",
                                info.viabtc_minutes_since_share != null ? `Pool last saw shares ${info.viabtc_minutes_since_share} min ago` : "",
                                info.last_offline_check ? `Last scheduler check: ${new Date(info.last_offline_check).toLocaleString()}` : "",
                              ].filter(Boolean).join("\n");
                              window.alert(`Why no WhatsApp alert for ${detail.worker || detail.ip}?\n\n${reasons}\n\n${extra}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Diagnostic failed");
                            }
                          }}
                          title="Explain why no WhatsApp alert was sent for this machine"
                          aria-label="Why no alert?"
                          className="p-1 rounded hover:bg-amber-500/20 text-gray-500 hover:text-amber-400 transition-colors text-[10px] px-1.5"
                          data-testid={`why-no-alert-${idx}`}
                        >
                          Why?
                        </button>
                        <button
                          onClick={() => handleHideWorker(detail)}
                          title="Hide this stale worker entry"
                          aria-label="Hide stale worker"
                          className="p-1 rounded hover:bg-[#EF4444]/20 text-gray-500 hover:text-[#EF4444] transition-colors"
                          data-testid={`hide-offline-worker-${idx}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(!machineMonitor.offline_details || machineMonitor.offline_details.length === 0) && (
              <div className="bg-[#00E054]/10 border border-[#00E054]/30 rounded-xl p-4 flex items-center gap-3">
                <Wifi className="text-[#00E054]" size={24} />
                <div>
                  <span className="font-bold text-[#00E054]">All Machines Online</span>
                  <p className="text-sm text-gray-500">No offline machines detected</p>
                </div>
              </div>
            )}

            {/* API Errors Warning */}
            {machineMonitor.api_errors?.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="text-yellow-500" size={20} />
                  <span className="font-bold text-yellow-500">
                    API Connection Issues ({machineMonitor.api_errors.length} accounts)
                  </span>
                </div>
                <p className="text-sm text-gray-400 mb-2">
                  Some accounts couldn't be fetched. Whitelist IP: <code className="bg-gray-800 px-2 py-0.5 rounded">{machineMonitor.server_ip}</code>
                </p>
                <div className="flex flex-wrap gap-2">
                  {machineMonitor.api_errors.map((err, idx) => (
                    <span key={idx} className="text-xs bg-yellow-900/50 text-yellow-300 px-2 py-1 rounded">
                      {err.account}: {err.reason}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <WifiOff size={48} className="mx-auto mb-4 opacity-50" />
            <p>Could not load machine status</p>
            <p className="text-sm mt-1">{machineMonitor?.error || "Check ViaBTC API settings"}</p>
          </div>
        )}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-[#00E054]/10">
              <DollarSign className="text-[#00E054]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Collected</p>
              <h3 className="text-xl font-bold text-white">
                ${(stats?.total_collected || 0).toLocaleString()}
              </h3>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-[#EF4444]/10">
              <AlertCircle className="text-[#EF4444]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-400">Pending Payments</p>
              <h3 className="text-xl font-bold text-white">
                ${(stats?.total_pending || 0).toLocaleString()}
              </h3>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-[#7C3AED]/10">
              <Cpu className="text-[#7C3AED]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Machines</p>
              <h3 className="text-xl font-bold text-white">
                {(() => {
                  // Live pool-reported count: ViaBTC (LTC+KAS+ZEC) + F2Pool. Falls
                  // back to the inventory count while the monitor is still loading
                  // so it never flashes 0.
                  const st = machineMonitor?.stats || {};
                  const viabtcTotal = (st.ltc?.total || 0) + (st.kas?.total || 0) + (st.zec?.total || 0);
                  const f2poolTotal = f2poolBreakdown?.enabled ? (f2poolBreakdown.total || 0) : 0;
                  const poolTotal = viabtcTotal + f2poolTotal;
                  if (machineMonitor?.success) return poolTotal;
                  return Object.values(stats?.machine_counts || {}).reduce((a, b) => a + b, 0);
                })()}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment History Chart */}
        <div className="chart-container">
          <h3 className="text-lg font-bold text-white mb-4">Payment History</h3>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} />
                <YAxis stroke="#A1A1AA" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0F0F0F', 
                    border: '1px solid #27272A',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#FFFFFF' }}
                />
                <Bar dataKey="paid" name="Paid" fill="#00E054" radius={[4, 4, 0, 0]} />
                <Bar dataKey="unpaid" name="Unpaid" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="paused" name="Paused" fill="#EAB308" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No payment data yet
            </div>
          )}
        </div>

        {/* Machine Distribution */}
        <div className="chart-container">
          <h3 className="text-lg font-bold text-white mb-4">Machine Distribution</h3>
          {machineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={machineData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                  labelLine={{ stroke: '#A1A1AA' }}
                >
                  {machineData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0F0F0F', 
                    border: '1px solid #27272A',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No machines registered yet
            </div>
          )}
        </div>
      </div>

      {/* Live Machine Data (from PC Sync) */}
      {liveMachines.length > 0 && (
        <div className="bg-[#0F0F0F] rounded-xl border border-[#27272A] p-4 sm:p-6" data-testid="live-machine-data">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-cyan-500/10 shrink-0">
                <Monitor className="text-cyan-400" size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-white">Live Machine Control Panel</h2>
                <p className="text-sm text-gray-500">
                  {liveMachines.length} machines synced from MineFleet
                  {liveSearch.trim() && (
                    <span className="text-cyan-400"> &middot; {searchedLiveMachines.length} match "{liveSearch.trim()}"</span>
                  )}
                  {liveMachines[0]?.updated_at && (
                    <span> &middot; Last sync: {new Date(liveMachines[0].updated_at).toLocaleTimeString()}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                placeholder="Search name, IP, farm..."
                value={liveSearch}
                onChange={(e) => setLiveSearch(e.target.value)}
                className="bg-[#1A1A1A] border-[#27272A] text-sm w-full sm:w-48 h-9"
                data-testid="live-search"
              />
              <BoardLookup customersOnly={liveFilter === "customers"} />
              <div className="flex items-center bg-[#1A1A1A] border border-[#27272A] rounded-lg overflow-hidden">
                <button
                  onClick={() => setLiveFilter("customers")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    liveFilter === "customers" ? "bg-cyan-500 text-black" : "text-gray-400 hover:text-white"
                  }`}
                  data-testid="live-filter-customers"
                >
                  My Customers
                </button>
                <button
                  onClick={() => setLiveFilter("all")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    liveFilter === "all" ? "bg-cyan-500 text-black" : "text-gray-400 hover:text-white"
                  }`}
                  data-testid="live-filter-all"
                >
                  All
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportMachines}
                className="border-[#27272A] hover:bg-[#27272A] text-[#00E054]"
                data-testid="export-machines-csv"
                title="Download CSV backup of all IPs + worker names. Save this BEFORE doing a global wallet switch."
              >
                <Download size={14} className="mr-2" />
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleImportMachines}
                className="border-[#27272A] hover:bg-[#27272A] text-amber-400"
                data-testid="import-machines-csv"
                title="Restore worker names from a previously-exported CSV. Optionally pushes to miners."
              >
                <Upload size={14} className="mr-2" />
                Import
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSyncMismatch(false)}
                className="border-[#27272A] hover:bg-[#27272A]"
                data-testid="check-sync-mismatch"
                title="Find machines visible in ViaBTC pool but not synced from your local PC"
              >
                <AlertCircle size={14} className={`mr-2 ${mismatchLoading ? 'animate-pulse' : ''}`} />
                Sync Check
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHiddenPanel(s => !s)}
                className={`border-[#27272A] hover:bg-[#27272A] ${(hiddenMachines.length + offlineHiddenWorkers.length) > 0 ? 'text-amber-400 border-amber-500/40' : ''}`}
                data-testid="toggle-hidden-panel"
                title="Machines you removed via 'Remove from machine list', plus workers temporarily hidden while offline."
              >
                Hidden{(hiddenMachines.length + offlineHiddenWorkers.length) > 0 ? ` (${hiddenMachines.length + offlineHiddenWorkers.length})` : ""}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setLiveMachinesLoading(true); fetchLiveMachines().finally(() => setLiveMachinesLoading(false)); }}
                className="border-[#27272A] hover:bg-[#27272A]"
                data-testid="refresh-live-machines"
              >
                <RefreshCw size={14} className={`mr-2 ${liveMachinesLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {showHiddenPanel && (
            <div className="bg-[#0A0A0A] rounded-lg border border-amber-500/30 p-4 mb-4" data-testid="hidden-machines-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-bold text-amber-400">Hidden Machines ({hiddenMachines.length})</p>
                  <p className="text-xs text-gray-500">These IPs were removed via "Remove from machine list". The PC sync is blocked from re-creating them until you unhide.</p>
                </div>
                <button onClick={() => setShowHiddenPanel(false)} className="text-gray-500 hover:text-white text-xs">close</button>
              </div>
              {hiddenMachines.length === 0 ? (
                <p className="text-xs text-gray-600 italic py-2 text-center">No hidden machines</p>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {hiddenMachines.map((h) => (
                    <div key={h.ip} className="flex items-center justify-between bg-[#0F0F0F] border border-[#27272A] rounded px-3 py-2">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-cyan-300">{h.ip}</span>
                        {h.hidden_at && (
                          <span className="text-[10px] text-gray-500">hidden {new Date(h.hidden_at).toLocaleString()}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnhideMachine(h.ip)}
                        className="text-xs bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 px-3 py-1 rounded"
                        data-testid={`unhide-${h.ip}`}
                      >
                        Unhide
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-[#27272A]">
                <p className="text-sm font-bold text-amber-400">Hidden While Offline ({offlineHiddenWorkers.length})</p>
                <p className="text-xs text-gray-500 mb-2">These workers were temporarily hidden while offline. They reappear on their own when they come back online — restore one here to show it sooner.</p>
                {offlineHiddenWorkers.length === 0 ? (
                  <p className="text-xs text-gray-600 italic py-2 text-center">None hidden while offline</p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {offlineHiddenWorkers.map((h) => (
                      <div key={h.worker} className="flex items-center justify-between bg-[#0F0F0F] border border-[#27272A] rounded px-3 py-2">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-cyan-300">{h.worker}</span>
                          {h.hidden_at && (
                            <span className="text-[10px] text-gray-500">hidden {new Date(h.hidden_at).toLocaleString()}</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleOfflineUnhide(h.worker)}
                          className="text-xs bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 px-3 py-1 rounded"
                          data-testid={`offline-unhide-${h.worker}`}
                        >
                          Restore
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#27272A]">
              <p className="text-xs text-gray-500">Online{liveSearch.trim() && <span className="text-cyan-400"> · {liveSearch.trim()}</span>}</p>
              <p className="text-xl font-bold text-[#00E054]">{searchedLiveMachines.filter(m => m.status === 'online').length}</p>
            </div>
            <button
              className="bg-[#0A0A0A] rounded-lg p-3 border border-[#27272A] text-left hover:border-red-500/50 transition-colors"
              onClick={() => setShowOfflineModal(true)}
              data-testid="offline-crashed-tile"
            >
              <p className="text-xs text-gray-500">Offline / Crashed</p>
              <p className="text-xl font-bold text-red-400">{searchedLiveMachines.filter(m => m.status !== 'online').length}</p>
            </button>
            <button
              className={`bg-[#0A0A0A] rounded-lg p-3 border text-left transition-colors ${boardsOnly ? 'border-amber-500' : 'border-[#27272A] hover:border-amber-500/50'}`}
              onClick={() => setBoardsOnly(v => !v)}
              title="Online, but hashing on fewer hashboards than it should (20+ min). Click to show only these."
              data-testid="missing-boards-tile"
            >
              <p className="text-xs text-gray-500">Missing boards {boardsOnly && '· showing'}</p>
              <p className={`text-xl font-bold ${searchedLiveMachines.some(m => m.boards_short) ? 'text-amber-400' : 'text-gray-600'}`}>
                {searchedLiveMachines.filter(m => m.boards_short).length}
              </p>
            </button>
            <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#27272A]">
              <p className="text-xs text-gray-500">Avg Temperature</p>
              <p className="text-xl font-bold text-orange-400">
                {searchedLiveMachines.filter(m => m.temperature > 0).length > 0 
                  ? Math.round(searchedLiveMachines.filter(m => m.temperature > 0).reduce((s, m) => s + m.temperature, 0) / searchedLiveMachines.filter(m => m.temperature > 0).length)
                  : 0}°C
              </p>
            </div>
            <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#27272A]">
              <p className="text-xs text-gray-500">Total Hashrate</p>
              <p className="text-xl font-bold text-cyan-400">
                {fmtGH(searchedLiveMachines.reduce((s, m) => s + (m.hashrate || 0), 0))}
              </p>
            </div>
          </div>

          {/* Sync Mismatch Panel */}
          {mismatchOpen && mismatch && (
            <div className="bg-[#0A0A0A] rounded-lg border border-amber-500/30 p-4 mb-4" data-testid="sync-mismatch-panel">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertCircle size={16} className="text-amber-400" />
                  <span className="text-sm font-bold text-white">Sync Mismatch Report</span>
                  <span className="text-xs text-gray-500">
                    Pools: {mismatch.viabtc_total} ViaBTC{mismatch.f2pool_total != null ? ` + ${mismatch.f2pool_total} F2Pool` : ""} online workers · Local: {mismatch.pc_total} customer machines
                    {mismatch.mode && <span className="ml-1 opacity-60">({mismatch.mode})</span>}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fetchSyncMismatch(true)}
                    className="text-xs text-cyan-400 hover:text-cyan-300 px-2 py-1"
                    data-testid="refresh-sync-mismatch"
                  >
                    <RefreshCw size={12} className={`inline mr-1 ${mismatchLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    onClick={() => setMismatchOpen(false)}
                    className="text-gray-500 hover:text-white p-1"
                    aria-label="Close"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

              {!mismatch.success && (
                <p className="text-sm text-red-400">{mismatch.error || "Failed to compare"}</p>
              )}

              {mismatch.success && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* In ViaBTC pool but missing from PC sync */}
                  <div className="bg-[#1A1A1A] rounded-lg p-3 border border-amber-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-amber-400">
                        In Pool but NOT synced locally
                      </span>
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                        {mismatch.viabtc_only?.length || 0}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 mb-2">
                      These miners are hashing in a pool but your PC sync can't see their IPs (offline LAN, blocked port, wrong subnet).
                    </p>
                    {(mismatch.viabtc_only?.length || 0) === 0 ? (
                      <p className="text-xs text-gray-500 italic">All pool workers are accounted for locally ✓</p>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {mismatch.viabtc_only.map((w, i) => (
                          <div key={`vbtc-${i}`} className="flex items-center justify-between bg-[#0A0A0A] rounded px-2 py-1.5">
                            <div className="min-w-0 flex-1">
                              <span className="text-xs font-medium text-white">{w.worker_name}</span>
                              <span className="text-[10px] text-gray-500 ml-2">({w.account})</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {w.pool && <span className={`text-[10px] ${w.pool === 'F2Pool' ? 'text-[#F59E0B]' : 'text-[#00C2FF]'}`}>{w.pool}</span>}
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                w.coin === 'LTC' ? 'bg-gray-700 text-gray-300' : 'bg-teal-900 text-teal-300'
                              }`}>{w.coin}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                w.status === 'online' ? 'bg-[#00E054]/10 text-[#00E054]' : 'bg-red-500/10 text-red-400'
                              }`}>{w.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* In PC sync but missing from ViaBTC */}
                  <div className="bg-[#1A1A1A] rounded-lg p-3 border border-cyan-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-cyan-400">
                        Synced locally but NOT in pool
                      </span>
                      <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">
                        {mismatch.pc_only?.length || 0}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-500 mb-2">
                      These miners are reachable on your LAN but neither ViaBTC nor F2Pool sees a worker — likely on the wrong pool or just rebooted.
                    </p>
                    {(mismatch.pc_only?.length || 0) === 0 ? (
                      <p className="text-xs text-gray-500 italic">All local machines are visible in the pool ✓</p>
                    ) : (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {mismatch.pc_only.map((m, i) => (
                          <div key={`pc-${i}`} className="flex items-center justify-between bg-[#0A0A0A] rounded px-2 py-1.5">
                            <div className="min-w-0 flex-1">
                              <span className="text-xs font-mono text-cyan-300">{m.ip}</span>
                              <span className="text-[10px] text-white ml-2">{m.worker_name || '—'}</span>
                              {m.farm && <span className="text-[10px] text-purple-400 ml-1">({m.farm})</span>}
                            </div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              m.status === 'online' ? 'bg-[#00E054]/10 text-[#00E054]' : 'bg-red-500/10 text-red-400'
                            }`}>{m.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Wallet Switch Controls */}
          <div className="bg-[#0A0A0A] rounded-lg border border-[#27272A] p-4 mb-4" data-testid="wallet-switch">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Zap size={16} className={walletSwitch.switched ? "text-[#F59E0B]" : "text-gray-500"} />
                <span className="text-sm font-medium text-white">Worker Switch</span>
                {walletSwitch.switched && (
                  <span className="text-xs bg-[#F59E0B]/20 text-[#F59E0B] px-2 py-0.5 rounded-full">
                    Active → {walletSwitch.new_worker}
                  </span>
                )}
              </div>
              
              {!walletSwitch.switched ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => { setSelectMode(!selectMode); setSelectedMachines(new Set()); }}
                    className={`text-xs px-3 py-1.5 rounded transition-colors ${selectMode ? 'bg-cyan-500 text-black' : 'bg-[#1A1A1A] text-gray-400 border border-[#27272A]'}`}
                  >
                    {selectMode ? `${selectedMachines.size} selected` : "Select Machines"}
                  </button>
                  <Input
                    placeholder="New worker name..."
                    value={newWallet}
                    onChange={(e) => setNewWallet(e.target.value)}
                    className="bg-[#1A1A1A] border-[#27272A] text-sm w-48 h-8"
                  />
                  <Button
                    size="sm"
                    disabled={!newWallet.trim()}
                    className="bg-[#F59E0B] text-black text-xs"
                    onClick={async () => {
                      const ips = selectMode && selectedMachines.size > 0 ? Array.from(selectedMachines) : [];
                      const target = ips.length > 0 ? `${ips.length} selected machines` : "ALL customer machines";
                      if (!window.confirm(`Switch ${target} to worker "${newWallet}"?`)) return;
                      try {
                        const res = await axios.post(`${API}/machine-data/switch-workers`, { new_worker: newWallet, ips });
                        toast.success(`Switch queued for ${res.data.machines} machines. PC will execute in ~2 min.`);
                        setSelectMode(false);
                        setSelectedMachines(new Set());
                        fetchLiveMachines();
                      } catch (e) { toast.error("Failed"); }
                    }}
                    data-testid="switch-workers-btn"
                  >
                    Switch {selectMode && selectedMachines.size > 0 ? `(${selectedMachines.size})` : "All"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    className="bg-[#00E054] text-black text-xs"
                    onClick={async () => {
                      if (!window.confirm("Restore all workers back to original?")) return;
                      try {
                        const res = await axios.post(`${API}/machine-data/restore-workers`);
                        toast.success(`Restore queued for ${res.data.restored} machines. PC will execute in ~2 min.`);
                        fetchLiveMachines();
                      } catch (e) { toast.error("Failed"); }
                    }}
                    data-testid="restore-workers-btn"
                  >
                    Restore Original Workers
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs border-[#27272A] text-gray-300 hover:text-white"
                    onClick={async () => {
                      if (!window.confirm("Already reverted these workers another way?\n\nThis just clears the banner so you can start a new switch. It does NOT send any commands to the miners.")) return;
                      try {
                        await axios.post(`${API}/machine-data/clear-switch`);
                        toast.success("Cleared. You can start a new switch now.");
                        fetchLiveMachines();
                      } catch (e) { toast.error("Failed to clear"); }
                    }}
                    title="I already reverted these workers — just clear the banner"
                  >
                    Already reverted — clear
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-2 flex justify-end gap-4">
              <button
                onClick={async () => {
                  if (!window.confirm("Delete customer machines that haven't synced in 7+ days?\n\nThis permanently removes them and blocks that IP from auto-reappearing later — same as clicking Remove on each one. This also runs automatically once a day.")) return;
                  try {
                    const r = await axios.post(`${API}/machine-data/cleanup-stale`);
                    if (r.data.count > 0) {
                      toast.success(`Deleted ${r.data.count} machine(s) offline 7+ days: ${r.data.deleted.map(d => d.worker_name || d.ip).join(", ")}`);
                      fetchLiveMachines();
                    } else {
                      toast.info("Nothing to clean up — no customer machines offline 7+ days.");
                    }
                  } catch (e) { toast.error("Cleanup failed"); }
                }}
                className="text-[11px] text-gray-500 hover:text-white underline-offset-2 hover:underline"
                title="Delete customer machines offline for 7+ days (also runs automatically once a day)"
              >
                Clean up machines offline 7+ days
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Stop the offline/recovered alert spam for renamed or deleted workers?\n\nThis clears any 'ghost' workers (names that no longer exist on any miner) from the watcher's memory. Real machines are not affected.")) return;
                  try {
                    const r = await axios.post(`${API}/machine-data/clear-ghost-alerts`);
                    toast.success(`Cleared ${r.data.removed_count} ghost worker(s). Spam will stop.`);
                  } catch (e) { toast.error("Failed to clear ghosts"); }
                }}
                className="text-[11px] text-gray-500 hover:text-white underline-offset-2 hover:underline"
                title="Stop offline/recovered alerts for renamed or deleted workers"
              >
                Getting offline/recovered spam for old worker names? Clear ghost alerts
              </button>
            </div>
          </div>

          {/* Recent worker-change verifications */}
          {(() => {
            const cwList = recentCmds.filter(c => c.action === 'change_worker');
            if (cwList.length === 0) return null;
            const revertable = cwList.filter(c =>
              (c.previous_worker_name || '').trim() &&
              ['verifying', 'completed', 'failed_unverified'].includes(c.status)
            );
            const colorMap = {
              pending: 'bg-gray-500/20 text-gray-300 border-gray-500/40',
              delivered: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
              verifying: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
              completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
              failed_unverified: 'bg-red-500/15 text-red-300 border-red-500/40',
              expired: 'bg-red-500/15 text-red-300 border-red-500/40',
            };
            const labelMap = {
              pending: 'Queued',
              delivered: 'Sent to PC',
              verifying: 'Verifying…',
              completed: 'Verified',
              failed_unverified: 'FAILED',
              expired: 'Expired',
            };
            return (
              <div className="mb-4 p-3 rounded-lg border border-[#27272A] bg-[#0A0A0A]">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
                    Recent worker changes — {cwList.length} machine{cwList.length === 1 ? '' : 's'}
                  </h4>
                  <div className="flex items-center gap-2">
                    {revertable.length > 0 && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Revert ALL ${revertable.length} machine(s) back to their previous worker names?\n\nThis queues a fresh change_worker for each one.`)) return;
                          try {
                            const res = await axios.post(`${API}/machine-data/recent-commands/revert-all`);
                            toast.success(`Revert queued for ${res.data.reverted} machine(s)${res.data.skipped_count ? ` (skipped ${res.data.skipped_count})` : ''}`);
                            fetchRecentCmds();
                          } catch (e) { toast.error('Revert-all failed'); }
                        }}
                        className="text-[11px] px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25"
                      >
                        Revert all ({revertable.length})
                      </button>
                    )}
                    <button
                      onClick={fetchRecentCmds}
                      className="text-[10px] text-gray-500 hover:text-white"
                      title="Re-check status"
                    >Refresh</button>
                  </div>
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {cwList.slice(0, 30).map(c => {
                    const target = c.expected_worker_name || (c.params && c.params.worker_name) || '';
                    const previous = c.previous_worker_name || '';
                    const s = c.status || 'pending';
                    const ts = (c.created_at || '').replace('T', ' ').slice(0, 19);
                    const canRevert = previous && ['verifying', 'completed', 'failed_unverified'].includes(s);
                    return (
                      <div key={c.id} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-[#181818] last:border-0">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className={`px-2 py-0.5 rounded border whitespace-nowrap ${colorMap[s] || colorMap.pending}`}>
                            {labelMap[s] || s}
                          </span>
                          <span className="text-gray-400 truncate">
                            <span className="text-gray-500">{c.ip}</span>
                            <span className="mx-1">·</span>
                            {previous ? (
                              <>
                                <span className="text-gray-300">{previous}</span>
                                <span className="text-gray-500 mx-1">→</span>
                              </>
                            ) : (
                              <span className="text-gray-600 italic mr-1">(old name not captured) → </span>
                            )}
                            <span className="text-white">{target}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          <span className="text-[10px] text-gray-500 mr-1">{ts}</span>
                          {canRevert && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Send ${c.ip} back to "${previous}"?`)) return;
                                try {
                                  await axios.post(`${API}/machine-data/recent-commands/${c.id}/revert`);
                                  toast.success(`Revert queued for ${c.ip} → ${previous}`);
                                  fetchRecentCmds();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Revert failed');
                                }
                              }}
                              className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30"
                              title={`Revert to ${previous}`}
                            >
                              Revert
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/machine-data/recent-commands/${c.id}/dismiss`);
                                fetchRecentCmds();
                              } catch (e) { toast.error('Failed'); }
                            }}
                            className="text-gray-500 hover:text-white px-1"
                            title="Keep the new worker — remove from this list"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {cwList.some(c => c.status === 'failed_unverified') && (
                  <div className="mt-2 text-[11px] text-red-300">
                    At least one change did not take effect on the miner. This is a problem in the PC Auto-Monitor app, not the website.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Machine table — wide data grid, scrolls horizontally on phones rather
              than collapsing (admins need every column + the sort/select actions) */}
          <p className="text-[11px] text-gray-500 mb-1 md:hidden">← Swipe sideways to see all columns →</p>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <table className="w-full text-sm min-w-[920px]">
              <thead>
                <tr className="border-b border-[#27272A]">
                  {selectMode && (
                    <th className="py-2.5 px-2 w-8">
                      <input
                        type="checkbox"
                        className="accent-cyan-500 w-4 h-4"
                        checked={selectedMachines.size === visibleLiveMachines.length && visibleLiveMachines.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMachines(new Set(visibleLiveMachines.map(m => m.ip)));
                          } else {
                            setSelectedMachines(new Set());
                          }
                        }}
                      />
                    </th>
                  )}
                  <th onClick={() => handleSort("status")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Status<SortArrow k="status" /></th>
                  <th onClick={() => handleSort("ip")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">IP<SortArrow k="ip" /></th>
                  <th onClick={() => handleSort("worker")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Worker<SortArrow k="worker" /></th>
                  <th onClick={() => handleSort("model")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Model<SortArrow k="model" /></th>
                  <th onClick={() => handleSort("farm")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Farm<SortArrow k="farm" /></th>
                  <th onClick={() => handleSort("hashrate")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Hashrate<SortArrow k="hashrate" /></th>
                  <th onClick={() => handleSort("temp")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Temp<SortArrow k="temp" /></th>
                  <th onClick={() => handleSort("fan")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Fan Speed<SortArrow k="fan" /></th>
                  <th onClick={() => handleSort("uptime")} className="text-left py-2.5 px-3 text-xs text-gray-500 font-medium cursor-pointer select-none hover:text-gray-300 whitespace-nowrap">Uptime<SortArrow k="uptime" /></th>
                  <th className="text-right py-2.5 px-3 text-xs text-gray-500 font-medium whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleLiveMachines
                  .map((m, idx) => {
                    const isOnline = m.status === 'online';
                    const isHot = m.temperature > 80;
                    return (
                      <tr key={m.ip || idx} className={`border-b border-[#27272A]/50 hover:bg-[#1A1A1A] transition-colors ${
                        m.is_customer ? 'bg-[#00E054]/5' : ''
                      } ${selectedMachines.has(m.ip) ? 'bg-cyan-500/10' : ''}`}>
                        {selectMode && (
                          <td className="py-2 px-2 w-8">
                            <input
                              type="checkbox"
                              className="accent-cyan-500"
                              checked={selectedMachines.has(m.ip)}
                              onChange={(e) => {
                                const next = new Set(selectedMachines);
                                if (e.target.checked) next.add(m.ip);
                                else next.delete(m.ip);
                                setSelectedMachines(next);
                              }}
                            />
                          </td>
                        )}
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                            isOnline ? 'bg-[#00E054]/10 text-[#00E054]' : 
                            m.status === 'crashed' ? 'bg-red-500/10 text-red-400' :
                            'bg-gray-500/10 text-gray-400'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-[#00E054]' : m.status === 'crashed' ? 'bg-red-400' : 'bg-gray-400'}`} />
                            {m.status || 'unknown'}
                          </span>
                          {m.stale && m.seconds_since_update && (
                            <div className="text-[10px] text-amber-400 mt-0.5" title={`Last update ${m.seconds_since_update}s ago`}>
                              {m.seconds_since_update >= 86400
                                ? `${Math.floor(m.seconds_since_update / 86400)}d ${Math.floor((m.seconds_since_update % 86400) / 3600)}h ago`
                                : m.seconds_since_update >= 3600
                                  ? `${Math.floor(m.seconds_since_update / 3600)}h ${Math.floor((m.seconds_since_update % 3600) / 60)}m ago`
                                  : `${Math.floor(m.seconds_since_update / 60)}m ago`}
                            </div>
                          )}
                          {m.in_repair && (
                            <div className="inline-flex items-center gap-1 mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 border border-orange-500/40"
                                 title="Marked as sent to repair — clears automatically when it reports online again">
                              <Wrench size={9} /> in repair
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 font-mono text-xs text-gray-300">
                          <a
                            href={`http://${m.ip}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#00E054] hover:underline"
                            title="Open this machine's own dashboard (needs Tailscale/VPN access to the farm network)"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {m.ip}
                          </a>
                          {(m.mac || m.sn) && (
                            <div className="text-[10px] text-gray-600 mt-0.5" title={[m.mac && `MAC: ${m.mac}`, m.sn && `SN: ${m.sn}`].filter(Boolean).join(" · ")}>
                              {m.mac || ''}{m.mac && m.sn ? ' · ' : ''}{m.sn || ''}
                            </div>
                          )}
                          {m.psu_sn && (
                            <div className="text-[10px] text-gray-600" title={`PSU SN: ${m.psu_sn}`}>
                              PSU: {m.psu_sn}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-white font-medium text-xs">
                          <span
                            className="cursor-pointer hover:text-cyan-400 transition-colors"
                            onClick={() => {
                              const newName = window.prompt("Rename worker:", m.worker_name || "");
                              if (newName && newName !== m.worker_name) {
                                axios.post(`${API}/machine-data/rename`, { ip: m.ip, worker_name: newName })
                                  .then(() => { toast.success(`Renamed to ${newName}`); fetchLiveMachines(); })
                                  .catch(() => toast.error("Failed to rename"));
                              }
                            }}
                            title="Click to rename"
                          >{m.worker_name || '—'}</span>
                          {m.is_customer && <span className="ml-1 text-[10px] bg-[#00E054]/20 text-[#00E054] px-1 rounded">customer</span>}
                          {m.original_worker && (
                            <div className="flex items-center gap-1 mt-0.5 text-[10px] text-amber-400/80">
                              <span title="Original worker before the temporary switch">was: {m.original_worker}</span>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Forget original worker "${m.original_worker}" for ${m.ip}?\n\nThis only clears the saved memory — it does NOT change the miner.`)) return;
                                  try {
                                    await axios.post(`${API}/machine-data/clear-original`, { ip: m.ip });
                                    toast.success("Original worker memory cleared");
                                    fetchLiveMachines();
                                  } catch (err) {
                                    toast.error("Failed to clear");
                                  }
                                }}
                                title="Forget original worker memory"
                                className="p-0.5 rounded hover:bg-amber-500/20 hover:text-amber-300 transition-colors"
                                data-testid={`clear-original-${m.ip}`}
                              >
                                <X size={10} />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-400 text-xs">{m.model || '—'}</td>
                        <td className="py-2 px-3 text-purple-400 text-xs">{m.farm || '—'}</td>
                        <td className="py-2 px-3 text-cyan-400 font-mono text-xs">
                          <div className="flex items-center gap-1.5">
                            <span>{m.hashrate ? fmtGH(m.hashrate) : '—'}</span>
                            {m.boards_short && (
                              <span
                                className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"
                                title={`Only ${m.boards_ok} of ${m.boards_expected} hashboards are hashing`}
                              >
                                {m.boards_ok}/{m.boards_expected} boards
                              </span>
                            )}
                            {m.hashrate_flagged && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40"
                                title={`Down ${Math.round((1 - m.hashrate / m.hashrate_avg_6h) * 100)}% vs. its 6h average (${m.hashrate_avg_6h.toFixed(1)} GH/s), for ${m.hashrate_drop_minutes}+ min`}
                              >
                                ▼ {Math.round((1 - m.hashrate / m.hashrate_avg_6h) * 100)}%
                              </span>
                            )}
                          </div>
                          {m.hashrate_avg_6h > 0 && (
                            <div className="text-[10px] text-gray-500 mt-0.5">avg 6h: {fmtGH(m.hashrate_avg_6h)}</div>
                          )}
                        </td>
                        <td className={`py-2 px-3 font-mono text-xs ${isHot ? 'text-red-400' : m.temperature > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
                          {m.temperature > 0 ? `${m.temperature}°C` : '—'}
                          {isHot && ' 🔥'}
                        </td>
                        <td className="py-2 px-3 text-gray-400 font-mono text-xs">{m.fan_speed > 0 ? `${m.fan_speed} RPM` : '—'}</td>
                        <td className="py-2 px-3 text-gray-500 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span>{m.uptime || '—'}</span>
                            {m.crash_count_24h > 0 && (
                              <span
                                className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded ${
                                  m.crash_count_24h >= 5
                                    ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                                    : m.crash_count_24h >= 2
                                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                      : 'bg-gray-500/15 text-gray-300 border border-gray-500/30'
                                }`}
                                title={`Crashed/restarted ${m.crash_count_24h} time(s) in the last 24 hours`}
                              >
                                ⟳ {m.crash_count_24h}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {liveFilter === "all" && !m.is_customer && (
                              <>
                              <button
                                onClick={async () => {
                                  const name = m.worker_name || m.ip;
                                  if (!window.confirm(`Add "${name}" as a temporary customer?`)) return;
                                  try {
                                    const custRes = await axios.post(`${API}/customers`, { name: name, phone: "", machines: [], total_cost: 0, total_fee: 0, status: "active", notes: "Whitelisted from Live Panel" });
                                    const custId = custRes.data.id;
                                    await axios.post(`${API}/customer-accounts`, { customer_id: custId, username: name.toLowerCase().replace(/[^a-z0-9]/g, ''), password: "0000", worker_name: m.worker_name || "" });
                                    toast.success(`${name} added as customer!`);
                                    fetchLiveMachines();
                                  } catch (e) { toast.error("Failed to add"); }
                                }}
                                className="text-xs bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 px-2 py-1 rounded transition-colors"
                                data-testid={`whitelist-${m.ip}`}
                              >
                                + Whitelist
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Remove ${m.worker_name || m.ip} from machine list?`)) return;
                                  try {
                                    await axios.delete(`${API}/machine-data/${encodeURIComponent(m.ip)}`);
                                    toast.success("Machine removed");
                                    fetchLiveMachines();
                                  } catch (e) { toast.error("Failed"); }
                                }}
                                className="text-xs bg-gray-500/10 text-gray-400 hover:bg-red-500/20 hover:text-red-400 px-2 py-1 rounded transition-colors"
                              >
                                Remove
                              </button>
                              </>
                            )}
                            {m.is_customer && (
                              <button
                                onClick={async () => {
                                  const name = m.worker_name || m.ip;
                                  const typed = window.prompt(
                                    `Un-whitelist "${name}"?\n\nThe customer will be ARCHIVED (hidden from your list) but their payment history will be preserved. You can restore them anytime from Customers → Archived.\n\nType the name to confirm:`
                                  );
                                  if (typed === null) return;
                                  if (typed.trim().toLowerCase() !== String(name).trim().toLowerCase()) {
                                    toast.error("Name didn't match — cancelled");
                                    return;
                                  }
                                  try {
                                    // Archive customer (soft-delete preserves payments) and remove portal account + machine cache
                                    let archived = false;
                                    const accs = await axios.get(`${API}/customer-accounts`);
                                    const acc = accs.data.find(a => a.worker_name?.toLowerCase() === (m.worker_name || '').toLowerCase());
                                    if (acc) {
                                      await axios.delete(`${API}/customer-accounts/${acc.id}`);
                                      if (acc.customer_id) {
                                        try {
                                          await axios.delete(`${API}/customers/${acc.customer_id}`);
                                          archived = true;
                                        } catch(e) {}
                                      }
                                    } else {
                                      // Fallback: try to find customer by name match
                                      try {
                                        const cs = await axios.get(`${API}/customers`);
                                        const cust = cs.data.find(c => c.name?.toLowerCase() === String(name).toLowerCase());
                                        if (cust) {
                                          await axios.delete(`${API}/customers/${cust.id}`);
                                          archived = true;
                                        }
                                      } catch(e) {}
                                    }
                                    await axios.delete(`${API}/machine-data/${encodeURIComponent(m.ip)}`);
                                    if (archived) {
                                      toast.success(`${name} archived — restore anytime from Customers → Archived`);
                                    } else {
                                      toast.success(`${name} removed from live panel`);
                                    }
                                    fetchLiveMachines();
                                  } catch (e) { toast.error("Failed"); }
                                }}
                                className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-2 py-1 rounded transition-colors"
                              >
                                Un-whitelist
                              </button>
                            )}
                            {m.worker_name && (m.stale || ["offline", "crashed", "unactive"].includes((m.status || "").toLowerCase())) && (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Hide "${m.worker_name}" from the list while it is offline?\n\nIt is NOT un-whitelisted — it will automatically reappear the moment it comes back online.`)) return;
                                  try {
                                    await axios.post(`${API}/machine-data/offline-hide`, { worker: m.worker_name });
                                    toast.success(`${m.worker_name} hidden while offline`);
                                    fetchLiveMachines();
                                  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                                }}
                                className="text-xs bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 px-2 py-1 rounded transition-colors"
                                data-testid={`offline-hide-${m.ip}`}
                              >
                                Hide
                              </button>
                            )}
                            {(m.in_repair || m.stale || ["offline", "crashed", "unactive"].includes((m.status || "").toLowerCase())) && (
                              <button
                                onClick={() => m.in_repair
                                  ? handleRepairUnpin({ ip: m.ip, worker_name: m.worker_name })
                                  : handleRepairPin({ ip: m.ip, worker_name: m.worker_name })}
                                title={m.in_repair
                                  ? "Remove the in-repair mark"
                                  : "Mark as sent to repair — clears automatically when it reports online again"}
                                className={`text-xs px-2 py-1 rounded transition-colors ${
                                  m.in_repair
                                    ? "bg-orange-500/20 text-orange-300 hover:bg-orange-500/30"
                                    : "bg-orange-500/10 text-orange-400 hover:bg-orange-500/20"
                                }`}
                                data-testid={`repair-pin-${m.ip}`}
                              >
                                <Wrench size={12} className="inline mr-1" />
                                {m.in_repair ? "In repair ✕" : "Repair"}
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Reboot machine at ${m.ip}?`)) return;
                                try {
                                  const res = await axios.post(`${API}/machine-data/command`, { ip: m.ip, action: 'reboot' });
                                  if (res.data.success) toast.success(`Reboot command queued for ${m.ip}`);
                                  else toast.error('Failed to queue command');
                                } catch (e) { toast.error('Failed'); }
                              }}
                              className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-2 py-1 rounded transition-colors"
                              data-testid={`reboot-${m.ip}`}
                            >
                              <RotateCcw size={12} className="inline mr-1" />
                              Reboot
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          
          {/* No data notice */}
          {liveMachines.length === 0 && (
            <p className="text-center text-gray-500 py-8">No machine data yet. Run Mining Fleet Pro on your farm PC to start syncing (Settings → Download for Windows).</p>
          )}
        </div>
      )}

      {/* Manual Machine Roster — hand-maintained per-customer counter */}
      <div className="bg-gradient-to-r from-[#0F0F0F] to-[#1A1A1A] rounded-xl border border-[#27272A] p-4 sm:p-6">
        <button
          onClick={() => { const n = !rosterOpen; setRosterOpen(n); if (n && !roster) fetchRoster(); }}
          className="w-full flex items-center justify-between gap-3"
          data-testid="roster-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-[#00E054]/10 shrink-0"><Users className="text-[#00E054]" size={22} /></div>
            <div className="min-w-0 text-left">
              <h2 className="text-lg sm:text-xl font-bold text-white">Customer Machine List</h2>
              <p className="text-sm text-gray-500">Manual count — add customers &amp; mark machines online yourself</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {roster?.totals && (
              <span className="text-xs text-gray-400 hidden sm:inline">
                <span className="text-[#00E054] font-semibold">{roster.totals.online}</span> online ·{" "}
                <span className="text-[#EF4444] font-semibold">{roster.totals.offline}</span> offline ·{" "}
                {roster.totals.machines} total
              </span>
            )}
            {rosterOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
          </div>
        </button>

        {rosterOpen && (
          <div className="mt-4">
            {/* Totals summary */}
            {roster?.totals && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setRosterStatusFilter(null)}
                  className={`bg-[#0A0A0A] rounded-lg border px-3 py-2 text-center transition-colors ${
                    rosterStatusFilter === null ? "border-white/40" : "border-[#1F1F1F] hover:border-[#3F3F3F]"
                  }`}
                  data-testid="roster-filter-total"
                >
                  <div className="text-xl font-bold text-white">{roster.totals.machines}</div>
                  <div className="text-[10px] text-gray-500 uppercase">Total machines</div>
                </button>
                <button
                  type="button"
                  onClick={() => setRosterStatusFilter(rosterStatusFilter === "online" ? null : "online")}
                  className={`bg-[#00E054]/5 rounded-lg border px-3 py-2 text-center transition-colors ${
                    rosterStatusFilter === "online" ? "border-[#00E054]" : "border-[#00E054]/20 hover:border-[#00E054]/50"
                  }`}
                  data-testid="roster-filter-online"
                >
                  <div className="text-xl font-bold text-[#00E054]">{roster.totals.online}</div>
                  <div className="text-[10px] text-gray-500 uppercase">Online</div>
                </button>
                <button
                  type="button"
                  onClick={() => setRosterStatusFilter(rosterStatusFilter === "offline" ? null : "offline")}
                  className={`bg-[#EF4444]/5 rounded-lg border px-3 py-2 text-center transition-colors ${
                    rosterStatusFilter === "offline" ? "border-[#EF4444]" : "border-[#EF4444]/20 hover:border-[#EF4444]/50"
                  }`}
                  data-testid="roster-filter-offline"
                >
                  <div className="text-xl font-bold text-[#EF4444]">{roster.totals.offline}</div>
                  <div className="text-[10px] text-gray-500 uppercase">Offline</div>
                </button>
              </div>
            )}

            {/* Per-model breakdown — how many L9, L7, L1 ... across every customer */}
            {roster?.totals?.by_model?.length > 0 && (
              <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] p-3 mb-3">
                <div className="text-[10px] text-gray-500 uppercase mb-2">By model</div>
                <div className="flex flex-wrap gap-2">
                  {roster.totals.by_model.map((m) => (
                    <button
                      key={m.model}
                      type="button"
                      onClick={() => setRosterModelFilter(rosterModelFilter === m.model ? null : m.model)}
                      className={`text-left rounded-lg px-3 py-2 min-w-[96px] border transition-colors ${
                        rosterModelFilter === m.model
                          ? "bg-[#00E054]/10 border-[#00E054]"
                          : "bg-[#111111] border-[#27272A] hover:border-[#3F3F3F]"
                      }`}
                      data-testid={`roster-model-${m.model}`}
                    >
                      <div className="text-xs font-semibold text-gray-400 uppercase">{m.model}</div>
                      <div className="text-xl font-bold text-white leading-tight">{m.machines}</div>
                      <div className="text-[10px] text-gray-500">
                        <span className="text-[#00E054] font-semibold">{m.online}</span> on ·{" "}
                        <span className="text-[#EF4444] font-semibold">{m.offline}</span> off
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Not-in-your-list: live-online machines missing from the manual roster */}
            {rosterReconcile?.unlisted?.length > 0 && (
              <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="text-amber-400" size={16} />
                  <span className="text-sm font-semibold text-amber-400">
                    Online now but not in your list ({rosterReconcile.unlisted_machines} machine{rosterReconcile.unlisted_machines !== 1 ? "s" : ""})
                  </span>
                </div>
                <div className="space-y-1">
                  {rosterReconcile.unlisted.map((u, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs bg-[#0A0A0A] rounded px-2 py-1.5">
                      <div className="min-w-0">
                        <span className="text-white font-medium">{u.customer || "(no matching customer)"}</span>
                        <span className="text-gray-500 ml-2">{u.count} online</span>
                        <span className="text-gray-600 ml-2 font-mono truncate">{u.workers.map(w => w.worker_name).slice(0, 3).join(", ")}{u.workers.length > 3 ? "…" : ""}</span>
                      </div>
                      <button
                        onClick={() => addUnlistedToRoster(u)}
                        className="shrink-0 text-xs bg-[#00E054]/10 text-[#00E054] hover:bg-[#00E054]/20 px-2 py-1 rounded transition-colors"
                        data-testid={`roster-add-unlisted-${i}`}
                      >
                        + Add
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add-customer row */}
            <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] p-3 mb-3 flex flex-wrap items-end gap-2">
              {rosterSuggestions.length > 0 && (
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] text-gray-500 uppercase">Pick a customer</label>
                  <select
                    value=""
                    onChange={(e) => {
                      const s = rosterSuggestions[Number(e.target.value)];
                      if (s) setNewRoster({ name: s.name, model: s.model || "", total: s.total || 0, online: s.total || 0 });
                    }}
                    className="bg-[#1A1A1A] border border-[#27272A] rounded-md text-sm text-white px-2 h-9 w-52"
                    data-testid="roster-suggestion-select"
                  >
                    <option value="">— suggest from customers —</option>
                    {rosterSuggestions.map((s, i) => (
                      <option key={i} value={i}>
                        {s.name}{s.model ? ` — ${s.total}× ${s.model}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-500 uppercase">Customer</label>
                <Input value={newRoster.name} onChange={(e) => setNewRoster({ ...newRoster, name: e.target.value })}
                       placeholder="e.g. jawadwk" className="bg-[#1A1A1A] border-[#27272A] text-sm w-40" data-testid="roster-new-name" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-500 uppercase">Model</label>
                <Input value={newRoster.model} onChange={(e) => setNewRoster({ ...newRoster, model: e.target.value })}
                       placeholder="L9" className="bg-[#1A1A1A] border-[#27272A] text-sm w-24" data-testid="roster-new-model" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-500 uppercase">Total</label>
                <Input type="number" min="0" value={newRoster.total} onChange={(e) => setNewRoster({ ...newRoster, total: e.target.value })}
                       className="bg-[#1A1A1A] border-[#27272A] text-sm w-20" data-testid="roster-new-total" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-gray-500 uppercase">Online</label>
                <Input type="number" min="0" value={newRoster.online} onChange={(e) => setNewRoster({ ...newRoster, online: e.target.value })}
                       className="bg-[#1A1A1A] border-[#27272A] text-sm w-20" data-testid="roster-new-online" />
              </div>
              <Button size="sm" onClick={addRosterEntry} className="bg-[#00E054] text-black hover:bg-[#00E054]/80" data-testid="roster-add-btn">
                + Add
              </Button>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Input placeholder="Search customer…" value={rosterSearch} onChange={(e) => setRosterSearch(e.target.value)}
                     className="bg-[#1A1A1A] border-[#27272A] text-sm max-w-xs" data-testid="roster-search" />
              <Button size="sm" variant="outline" onClick={fetchRoster} disabled={rosterLoading} className="border-[#27272A] hover:bg-[#27272A]">
                <RefreshCw size={14} className={`mr-2 ${rosterLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              {rosterModelFilter && (
                <button
                  onClick={() => setRosterModelFilter(null)}
                  className="text-xs px-2 py-1 rounded-md border border-[#00E054]/40 text-[#00E054] bg-[#00E054]/10"
                  data-testid="roster-model-clear"
                >
                  Model: {rosterModelFilter} ✕
                </button>
              )}
              {rosterStatusFilter && (
                <button
                  onClick={() => setRosterStatusFilter(null)}
                  className={`text-xs px-2 py-1 rounded-md border ${
                    rosterStatusFilter === "online"
                      ? "border-[#00E054]/40 text-[#00E054] bg-[#00E054]/10"
                      : "border-[#EF4444]/40 text-[#EF4444] bg-[#EF4444]/10"
                  }`}
                  data-testid="roster-filter-clear"
                >
                  Showing {rosterStatusFilter} only ✕
                </button>
              )}
            </div>

            {rosterLoading && !roster ? (
              <p className="text-center text-gray-500 py-6">Loading…</p>
            ) : !roster?.roster?.length ? (
              <p className="text-center text-gray-500 py-6">No customers yet — add one above.</p>
            ) : (() => {
              // Count how many entries share each (case-insensitive) name so
              // duplicates can be flagged red -- catches accidentally adding the
              // same customer twice.
              const nameCounts = {};
              roster.roster.forEach((c) => {
                const k = (c.name || "").trim().toLowerCase();
                if (k) nameCounts[k] = (nameCounts[k] || 0) + 1;
              });
              const dupCount = Object.values(nameCounts).filter((n) => n > 1).length;
              return (
              <div className="space-y-1.5">
                {dupCount > 0 && (
                  <div className="flex items-center gap-2 text-xs text-[#EF4444] mb-1">
                    <AlertTriangle size={14} /> {dupCount} duplicate customer name{dupCount !== 1 ? "s" : ""} — highlighted below
                  </div>
                )}
                {roster.roster
                  .filter((c) => !rosterSearch.trim() || (c.name || "").toLowerCase().includes(rosterSearch.toLowerCase()))
                  .filter((c) => {
                    if (!rosterModelFilter) return true;
                    // "Unspecified" is the bucket the backend uses for rows with
                    // no model, so it has to match empty here rather than a
                    // literal model name nobody typed.
                    const model = (c.model || "").trim();
                    return rosterModelFilter === "Unspecified" ? !model : model === rosterModelFilter;
                  })
                  .filter((c) => {
                    if (rosterStatusFilter === "online") return (c.online || 0) > 0;
                    if (rosterStatusFilter === "offline") return Math.max(0, (c.total || 0) - (c.online || 0)) > 0;
                    return true;
                  })
                  .map((c) => {
                    const offline = Math.max(0, (c.total || 0) - (c.online || 0));
                    const isDup = (c.name || "").trim() && nameCounts[(c.name || "").trim().toLowerCase()] > 1;
                    return (
                      <div key={c.id} className={`rounded-lg border px-3 py-2.5 flex flex-wrap items-center justify-between gap-2 ${
                        isDup ? "bg-[#EF4444]/10 border-[#EF4444]/50" : "bg-[#0A0A0A] border-[#1F1F1F]"
                      }`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {isDup && <AlertTriangle size={13} className="text-[#EF4444] shrink-0" title="Duplicate — this name is in the list more than once" />}
                          <span className={`text-sm font-medium truncate ${isDup ? "text-[#EF4444]" : "text-white"}`}>{c.name || "—"}</span>
                          <span className="text-xs text-gray-500">{c.total}× {c.model || "machine"}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Online counter with +/- */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 uppercase mr-1">Online</span>
                            <button
                              onClick={() => updateRosterEntry(c.id, { online: Math.max(0, (c.online || 0) - 1) })}
                              disabled={(c.online || 0) <= 0}
                              className="w-6 h-6 rounded bg-[#EF4444]/10 text-[#EF4444] hover:bg-[#EF4444]/20 disabled:opacity-30 disabled:cursor-not-allowed text-sm leading-none"
                              title="Mark one offline"
                              data-testid={`roster-online-minus-${c.id}`}
                            >−</button>
                            <span className="text-sm font-mono w-14 text-center">
                              <span className="text-[#00E054]">{c.online}</span>
                              <span className="text-gray-600"> / {c.total}</span>
                            </span>
                            <button
                              onClick={() => updateRosterEntry(c.id, { online: Math.min(c.total || 0, (c.online || 0) + 1) })}
                              disabled={(c.online || 0) >= (c.total || 0)}
                              className="w-6 h-6 rounded bg-[#00E054]/10 text-[#00E054] hover:bg-[#00E054]/20 disabled:opacity-30 disabled:cursor-not-allowed text-sm leading-none"
                              title="Mark one online"
                              data-testid={`roster-online-plus-${c.id}`}
                            >+</button>
                          </div>
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${offline > 0 ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-gray-500/10 text-gray-500'}`}>
                            {offline} offline
                          </span>
                          {/* Add / remove a machine (total) */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-500 uppercase mr-1">Machines</span>
                            <button
                              onClick={() => updateRosterEntry(c.id, { total: Math.max(0, (c.total || 0) - 1) })}
                              disabled={(c.total || 0) <= 0}
                              className="w-6 h-6 rounded bg-gray-600/20 text-gray-300 hover:bg-gray-600/40 disabled:opacity-30 disabled:cursor-not-allowed text-sm leading-none"
                              title="Remove a machine"
                              data-testid={`roster-total-minus-${c.id}`}
                            >−</button>
                            <button
                              onClick={() => updateRosterEntry(c.id, { total: (c.total || 0) + 1 })}
                              className="w-6 h-6 rounded bg-gray-600/20 text-gray-300 hover:bg-gray-600/40 text-sm leading-none"
                              title="Add a machine"
                              data-testid={`roster-total-plus-${c.id}`}
                            >+</button>
                          </div>
                          <button
                            onClick={() => deleteRosterEntry(c)}
                            className="p-1 rounded hover:bg-[#EF4444]/20 text-gray-500 hover:text-[#EF4444] transition-colors"
                            title="Delete customer from list"
                            data-testid={`roster-delete-${c.id}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Machine Counter Breakdown */}
      {machineData.length > 0 && (
        <div className="chart-container">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">Machine Counter</h3>
            <Button size="sm" variant="outline" className="border-[#27272A] text-gray-400 hover:text-white"
                    onClick={handleOpenManageGroups} data-testid="combine-types-btn">
              ⚙ Combine Types
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {machineData.map((machine, idx) => (
              <div
                key={machine.name}
                onClick={() => handleShowTypeBreakdown(machine.name)}
                className="p-4 rounded-lg border border-[#27272A] bg-[#0A0A0A] hover:border-[#00E054]/50 transition-colors cursor-pointer"
                data-testid={`machine-counter-${machine.name}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                  />
                  <span className="text-gray-400 text-sm">{machine.name}</span>
                </div>
                <p className="text-2xl font-bold text-white mt-2">{machine.value}</p>
                <p className="text-xs text-gray-500">machines</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Machine Type Breakdown Modal — which customers make up a counter card.
          Rendered via portal straight into document.body: the page wrapper's
          animate-fadeIn class leaves a lingering `transform: translateY(0)`
          after its animation finishes (animation-fill-mode: forwards), and
          ANY transform on an ancestor breaks `position: fixed` -- it stops
          centering in the actual viewport and centers within that ancestor's
          full scrollable height instead, so the modal could land far above
          your current scroll position. A portal escapes that entirely. */}
      {typeBreakdown && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div ref={typeBreakdownScrollRef} className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto" data-testid="machine-type-breakdown-modal">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#27272A] p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">{typeBreakdown.name} — by customer</h2>
              <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => setTypeBreakdown(null)}>✕</Button>
            </div>
            <div className="p-5 space-y-4">
              {typeBreakdown.loading ? (
                <p className="text-center text-gray-500 py-6">Loading...</p>
              ) : typeBreakdown.error ? (
                <p className="text-center text-red-400 py-6">Failed to load customers.</p>
              ) : typeBreakdown.rows.length === 0 ? (
                <p className="text-center text-gray-500 py-6">No customers have a {typeBreakdown.name} machine.</p>
              ) : (
                <>
                  {/* Paused vs active — total machine count, not customer count */}
                  <div className="flex flex-wrap gap-2 pb-3">
                    <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-[#0F0F0F] border border-[#27272A] text-gray-300">
                      Active <span className="text-[#00E054] font-bold">×{typeBreakdown.activeQty || 0}</span>
                    </span>
                    <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-[#0F0F0F] border border-[#27272A] text-gray-300">
                      Paused <span className="text-[#EAB308] font-bold">×{typeBreakdown.pausedQty || 0}</span>
                    </span>
                  </div>
                  {/* By price tier — how the merged count splits across $300/$275/free/etc variants */}
                  {typeBreakdown.byPrice.length > 0 && (
                    <div className="flex flex-wrap gap-2 pb-3 border-b border-[#27272A]">
                      {typeBreakdown.byPrice.map((p, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-lg text-xs font-medium bg-[#0F0F0F] border border-[#27272A] text-gray-300">
                          {p.fee === "unknown" ? "Unknown price" : p.fee == 0 ? "Free" : `$${p.fee}`} <span className="text-[#00E054] font-bold">×{p.qty}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* By customer — one row per exact variant they have (name + price) */}
                  <div className="space-y-2">
                    {typeBreakdown.rows.map((row, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-[#0F0F0F] rounded-lg border border-[#27272A]">
                        <span className="text-white font-medium">{row.customer_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-sm">
                            {row.quantity}x {row.machine_name}
                            {row.fee !== undefined && row.fee !== null && (
                              <span className="text-gray-600"> ({row.fee == 0 ? "Free" : `$${row.fee}`})</span>
                            )}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            row.status === 'active' ? 'status-paid' : 'status-paused'
                          }`}>
                            {row.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Combine Machine Types Modal — also portaled, same reason as above */}
      {manageGroupsOpen && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto" data-testid="combine-types-modal">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#27272A] p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">Combine Machine Types</h2>
              <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => setManageGroupsOpen(false)}>✕</Button>
            </div>
            <div className="p-5 space-y-6">
              {/* Existing groups */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Existing Combined Groups</h3>
                {machineTypeGroups.length === 0 ? (
                  <p className="text-sm text-gray-600">No custom groups yet — combine types below.</p>
                ) : (
                  <div className="space-y-2">
                    {machineTypeGroups.map((g) => (
                      editingGroupId === g.id ? (
                        <div key={g.id} className="p-3 bg-[#0F0F0F] rounded-lg border border-[#00E054]/40 space-y-3">
                          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                            {allMachineTypes.map((t) => (
                              <label key={t.id} className="flex items-center gap-2 p-2 bg-[#0A0A0A] rounded-lg border border-[#27272A] cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  checked={editGroupSelectedIds.includes(t.id)}
                                  onChange={() => toggleEditGroupType(t.id)}
                                  className="accent-[#00E054]"
                                />
                                <span className="text-white">{t.name}</span>
                                <span className="text-gray-500 text-xs ml-auto">${t.monthly_fee}</span>
                              </label>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              value={editGroupName}
                              onChange={(e) => setEditGroupName(e.target.value)}
                              className="bg-[#0A0A0A] border-[#27272A]"
                            />
                            <Button size="sm" className="bg-[#00E054] text-black hover:bg-[#00E054]/90 whitespace-nowrap"
                                    onClick={handleSaveGroupEdit}>
                              Save
                            </Button>
                            <Button size="sm" variant="outline" className="border-[#27272A] text-gray-400"
                                    onClick={() => setEditingGroupId(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div key={g.id} className="flex items-center justify-between p-3 bg-[#0F0F0F] rounded-lg border border-[#27272A]">
                          <div>
                            <span className="text-white font-medium">{g.group_name}</span>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {g.member_type_ids
                                .map(id => allMachineTypes.find(t => t.id === id)?.name || "?")
                                .join(" + ")}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="border-[#27272A] text-gray-400 hover:text-white"
                                    onClick={() => handleStartEditGroup(g)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                    onClick={() => handleDeleteGroup(g.id)}>
                              Ungroup
                            </Button>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>

              {/* Create new group */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Create a New Group</h3>
                <p className="text-xs text-gray-600 mb-3">Pick 2 or more types to combine into one Dashboard counter card.</p>
                <div className="grid grid-cols-2 gap-2 mb-3 max-h-48 overflow-y-auto pr-1">
                  {allMachineTypes.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 p-2 bg-[#0F0F0F] rounded-lg border border-[#27272A] cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={newGroupSelectedIds.includes(t.id)}
                        onChange={() => toggleNewGroupType(t.id)}
                        className="accent-[#00E054]"
                      />
                      <span className="text-white">{t.name}</span>
                      <span className="text-gray-500 text-xs ml-auto">${t.monthly_fee}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Combined group name, e.g. L9"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="bg-[#0F0F0F] border-[#27272A]"
                  />
                  <Button className="bg-[#00E054] text-black hover:bg-[#00E054]/90 whitespace-nowrap"
                          onClick={handleCreateGroup}>
                    Create Group
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Full Database Backup */}
      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <input
          type="file"
          id="backup-import"
          accept=".xlsx"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const mode = window.confirm(
              "Choose import mode:\n\nOK = MERGE (add to existing data, update duplicates)\nCancel = REPLACE (wipe everything and restore from backup)"
            ) ? "merge" : "replace";
            if (mode === "replace" && !window.confirm("WARNING: This will DELETE all existing data and replace with backup. Are you sure?")) {
              e.target.value = "";
              return;
            }
            const formData = new FormData();
            formData.append("file", file);
            toast.info("Importing backup...");
            try {
              const res = await axios.post(`${API}/import/full-backup?mode=${mode}`, formData);
              if (res.data.success) {
                const r = res.data.results;
                const summary = Object.entries(r).filter(([k]) => k !== "mode").map(([k, v]) => `${k}: ${v}`).join(", ");
                toast.success(`Import complete (${r.mode}): ${summary}`);
                fetchStats();
                fetchMachineMonitor(true);
              } else {
                toast.error(`Import failed: ${res.data.error}`);
              }
            } catch (err) {
              toast.error("Import failed: " + (err.response?.data?.detail || err.message));
            }
            e.target.value = "";
          }}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => document.getElementById("backup-import").click()}
          className="border-[#27272A] hover:bg-[#27272A] text-gray-400 hover:text-white text-xs"
          data-testid="import-backup-btn"
        >
          <ArrowUpRight size={14} className="mr-2" />
          Import Backup
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            toast.info("Generating backup...");
            window.open(`${API}/export/full-backup?admin_token=${encodeURIComponent(getAdminToken())}`, '_blank');
          }}
          className="border-[#27272A] hover:bg-[#27272A] text-gray-400 hover:text-white text-xs"
          data-testid="full-backup-btn"
        >
          <ArrowDownRight size={14} className="mr-2" />
          Export Full Backup
        </Button>
      </div>
    </div>
  );
};

export default Dashboard;
